import {
  Button,
  Checkbox,
  Input,
  message,
  Radio,
  Select,
  Typography,
} from "antd";
import { format } from "date-fns";
import { useFormik } from "formik";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getCategories } from "../../../../controller/services/common";
import {
  GetExpenseDetails,
  UpdateExpenses,
} from "../../../../controller/services/profile-stepper";
import useAvgValue from "../../../../hooks/useAvgValue";
import i18n from "../../../../localization/i18n";
import {
  setAnsweredQuestions,
  setInfoContent,
  setInnerStepper,
  setLoading,
  setStepperStatus,
  setTotalQuestions,
} from "../../../../redux/slices/common_slice";
import { routePaths } from "../../../../routing/routePaths";
import {
  formatNumber,
  getErrorDataClassName,
  handleKeyDown,
  isFalsyExceptZero,
} from "../../../../utils/helper";
import CreateProfileCardWrapper from "../../CreateProfileCardWrapper";
import CreateProfileCenterWrapper from "../../CreateProfileCenterWrapper";
import CreateProfileFooter from "../../CreateProfileFooter";
import CreateProfileHeader from "../../CreateProfileHeader";

const LivingType = ({ formik }) => {
  const userData = useSelector((state) => state.form.userData);
  const { t } = useTranslation();
  return (
    <>
      <CreateProfileCardWrapper>
        <h5 className="d-flex align-items-center justify-content-between">
          {userData?.livingInCanada === "yes" ? (
            <>
              {t("expenses.ques1.title")} <br />
              <br />
              {t("expenses.ques1.helperText")}
            </>
          ) : (
            "Which of the options below would best describe your living situation if you were to move to Canada?"
          )}
        </h5>
        <Radio.Group
          className="w-100"
          name="living"
          value={formik.values?.living}
          onChange={(e) => {
            if (e.target.value === "rent" || e.target.value === "other") {
              formik.setValues({
                ...formik.values,
                mortgage: "",
                homeInsurance: "",
                condoFee: "",
                taxPay: "",
                homeMaintenance: "",
              });
            } else if (e.target.value === "own") {
              formik.setValues({
                ...formik.values,
                rentAmount: "",
                tenantInsurance: "",
              });
            }
            formik.handleChange(e);
          }}
        >
          <ul className="list-inline mb-0">
            <li>
              <Radio value={"rent"}>{t("expenses.ques1.options.1")}</Radio>
            </li>
            <li>
              <Radio value={"own"}>{t("expenses.ques1.options.2")}</Radio>
            </li>
            <li>
              <Radio value={"other"}>{t("expenses.ques1.options.3")}</Radio>
            </li>
          </ul>
        </Radio.Group>
        {formik.errors["living"] && formik.touched["living"] && (
          <Typography textAlign={"left"} variant="body2" color="red">
            {formik.errors["living"]}
          </Typography>
        )}
      </CreateProfileCardWrapper>
      {formik.values?.living === "rent" && (
        <div className="row if-no-select-show-screen mb-bottom-24">
          <div className="col-12 col-sm-12 col-md-8 col-lg-8 col-xl-9">
            <h5 className="mb-0 w-auto">{t("expenses.resources.1")}</h5>
          </div>
          <div className="col-12 col-sm-12 col-md-4 col-lg-4 col-xl-3">
            <Button
              className="no-employee-btn"
              onClick={() => {
                window.open(
                  "https://ised-isde.canada.ca/site/office-consumer-affairs/en/buying-and-leasing-big-ticket-items/landlord-and-tenant-relations"
                );
              }}
            >
              {t("newTab")}
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

const Mortgage = ({ formik }) => {
  const userData = useSelector((state) => state.form.userData);
  const { t } = useTranslation();
  return (
    <>
      <CreateProfileCardWrapper>
        <h5 className="d-flex align-items-center">
          {userData?.livingInCanada === "yes"
            ? t("expenses.ques4.title")
            : "Enter your expected monthly mortgage payment."}
        </h5>
        <Input
          prefix={formik.values.mortgage ? "$" : " "}
          placeholder={t("expenses.commonPlaceholder")}
          name={"mortgage"}
          value={formatNumber(formik.values.mortgage)}
          onChange={formik.handleChange}
          onKeyDown={handleKeyDown}
          suffix={
            formik.values.mortgage ? (
              <img
                src="/assets/images/icons/primary-check-icon.svg"
                alt=""
                className=""
              />
            ) : (
              ""
            )
          }
          className={getErrorDataClassName("mortgage", formik)}
        />

        {formik.errors["mortgage"] && formik.touched["mortgage"] && (
          <Typography textAlign={"left"} variant="body2" color="red">
            {formik.errors["mortgage"]}
          </Typography>
        )}
      </CreateProfileCardWrapper>
    </>
  );
};

const Utilities = ({ formik, handleKeyPress }) => {
  const userData = useSelector((state) => state.form.userData);
  const { t } = useTranslation();
  const [avgValue, setAvgValue] = useState("");
  const { fetchAvgVal } = useAvgValue();
  useEffect(() => {
    handleFetchAvg("/city_cost_estimator");
  }, []);
  const handleFetchAvg = async (endpoint) => {
    const updatedVal = await fetchAvgVal(endpoint, "Utilities");
    setAvgValue(Math.round(updatedVal?.estimate) || 0);
  };
  return (
    <>
      <CreateProfileCardWrapper>
        <h5 className="d-flex align-items-center">
          {userData?.livingInCanada === "yes"
            ? t("expenses.ques10.title")
            : "How much do you think you’ll spend on utilities?"}
        </h5>
        <p>{t("expenses.ques10.helperText")}</p>
        <Input
          prefix={formik.values.utilities ? "$" : " "}
          placeholder={t("expenses.commonPlaceholder")}
          name={"utilities"}
          onChange={formik.handleChange}
          onKeyDown={handleKeyDown}
          value={formatNumber(formik.values.utilities)}
          min={0}
          className={getErrorDataClassName("utilities", formik)}
          suffix={
            formik.values.utilities ? (
              <img
                src="/assets/images/icons/primary-check-icon.svg"
                alt=""
                className=""
              />
            ) : (
              ""
            )
          }
        />
        {avgValue ? (
          <div className="avg-value-div d-flex  align-items-center justify-content-between w-100">
            <div className="avg-value-text">
              {t("expenses.resources.6", {
                value1: userData?.province || userData?.provinceNew,
                value2: avgValue,
              })}
            </div>
            <Button
              className="avg-value-btn"
              onClick={() => formik.setFieldValue("utilities", `${avgValue}`)}
            >
              {t("avgBtn")}
            </Button>
          </div>
        ) : (
          ""
        )}
        {formik.errors["utilities"] && formik.touched["utilities"] && (
          <Typography textAlign={"left"} variant="body2" color="red">
            {formik.errors["utilities"]}
          </Typography>
        )}
      </CreateProfileCardWrapper>
      <div className="row if-no-select-show-screen mb-bottom-24">
        <div className="col-12 col-sm-12 col-md-8 col-lg-8 col-xl-9">
          <h5 className="mb-0 w-auto">{t("expenses.resources.7")}</h5>
        </div>
        <div className="col-12 col-sm-12 col-md-4 col-lg-4 col-xl-3">
          <Button
            className="no-employee-btn"
            onClick={() =>
              window.open(
                "https://energyrates.ca/questions-to-ask-before-you-sign-an-energy-contract/"
              )
            }
          >
            {t("newTab")}
          </Button>
        </div>
      </div>
    </>
  );
};

const BasicExpenses = ({ formik, handleKeyPress }) => {
  const userData = useSelector((state) => state.form.userData);
  const { fetchAvgVal } = useAvgValue();
  const dispatch = useDispatch();
  const [avgVaues, setAvgValues] = useState({});
  const { t } = useTranslation();
  useEffect(() => {
    handleFetchAvg("/city_cost_estimator", "groceries");
    handleFetchAvg("/city_prices", "internetBill");
  }, []);
  const handleFetchAvg = async (endpoint, fieldName) => {
    let updatedVal;
    if (fieldName === "groceries") {
      updatedVal = await fetchAvgVal(endpoint, "Food at Home");
    } else if (fieldName === "internetBill") {
      updatedVal = await fetchAvgVal(endpoint, "Utilities");
      updatedVal = updatedVal?.find((item) =>
        item.item_name.includes("Internet")
      );
    }
    if (updatedVal) {
      setAvgValues((prev) => ({
        ...prev,
        [fieldName]:
          Math.round(updatedVal?.estimate) ||
          Math.round(updatedVal?.average_price) ||
          0,
      }));
    }
  };
  const getHouseHoldCount = () => {
    const foundChildren =
      (userData?.houseHold?.length > 0 &&
        userData?.houseHold?.find((i) => i.type === "children")?.count) ||
      0;
    const foundMembers =
      (userData?.houseHold?.length > 0 &&
        userData?.houseHold?.find((i) => i.type === "otherMembers")?.count) ||
      0;
    const isMyself =
      userData?.houseHold?.length > 0 &&
      userData?.houseHold?.find((i) => i?.type === "myself");
    const isPartner =
      userData?.houseHold?.length > 0 &&
      userData?.houseHold?.find((i) => i?.type === "partner");
    let totalMembers = Number(foundMembers) + Number(foundChildren);
    if (isMyself) {
      totalMembers = Number(totalMembers) + 1;
    }
    if (isPartner) {
      totalMembers = Number(totalMembers) + 1;
    }
    return totalMembers;
  };
  return (
    <div className="row mx-0 profile-second-card-row">
      <h5>
        {userData?.livingInCanada === "yes"
          ? t("expenses.ques11.title")
          : "How much do you think you’ll spend on the following expenses each month?"}
      </h5>
      <p>{t("expenses.ques11.helperText")}</p>
      <CreateProfileCardWrapper>
        <h5>{t("expenses.ques12.title")}</h5>
        <Input
          prefix={formik.values.cellphoneBill ? "$" : " "}
          placeholder={t("expenses.commonPlaceholder")}
          name={`cellphoneBill`}
          onChange={formik.handleChange}
          onKeyDown={handleKeyDown}
          value={formatNumber(formik.values.cellphoneBill)}
          suffix={
            formik.values.cellphoneBill ? (
              <img
                src="/assets/images/icons/primary-check-icon.svg"
                alt=""
                className=""
              />
            ) : (
              ""
            )
          }
          className={getErrorDataClassName("cellphoneBill", formik)}
        />
        <div className="avg-value-div d-flex  align-items-center justify-content-between w-100">
          <div className="avg-value-text">{t("expenses.resources.8")}</div>
          <Button
            className="avg-value-btn"
            onClick={() => formik.setFieldValue("cellphoneBill", 45)}
          >
            {t("avgBtn")}
          </Button>
        </div>
        {formik.errors["cellphoneBill"] && formik.touched["cellphoneBill"] && (
          <Typography textAlign={"left"} variant="body2" color="red">
            {formik.errors["cellphoneBill"]}
          </Typography>
        )}
      </CreateProfileCardWrapper>
      <div className="row if-no-select-show-screen mb-bottom-24">
        <div className="col-12 col-sm-12 col-md-8 col-lg-8 col-xl-9">
          <h5 className="mb-0 w-auto">
            {userData?.livingInCanada === "yes"
              ? t("expenses.resources.9")
              : "Curious about how to save on a Canadian cell phone plan? Before choosing a provider, use comparison websites to look at different prices."}
          </h5>
        </div>
        <div className="col-12 col-sm-12 col-md-4 col-lg-4 col-xl-3">
          <Button
            className="no-employee-btn"
            onClick={() => window.open("https://www.ratehub.ca/telecom/mobile")}
          >
            {t("newTab")}
          </Button>
        </div>
      </div>
      <CreateProfileCardWrapper>
        <h5>{t("expenses.ques13.title")}</h5>
        <Input
          prefix={formik.values.internetBill ? "$" : " "}
          placeholder={t("expenses.commonPlaceholder")}
          name={`internetBill`}
          onChange={formik.handleChange}
          onKeyDown={handleKeyDown}
          value={formatNumber(formik.values.internetBill)}
          suffix={
            formik.values.internetBill ? (
              <img
                src="/assets/images/icons/primary-check-icon.svg"
                alt=""
                className=""
              />
            ) : (
              ""
            )
          }
          className={getErrorDataClassName("internetBill", formik)}
        />
        {avgVaues?.internetBill && (
          <div className="avg-value-div d-flex  align-items-center justify-content-between w-100">
            <div className="avg-value-text">
              {t("expenses.resources.10", {
                value1: userData?.city || userData?.cityNew,
                value2: Math.round(avgVaues?.internetBill),
              })}
            </div>
            <Button
              className="avg-value-btn"
              onClick={() =>
                formik.setFieldValue(
                  "internetBill",
                  Math.round(avgVaues?.internetBill || 0)
                )
              }
            >
              {t("avgBtn")}
            </Button>
          </div>
        )}
        {formik.errors["internetBill"] && formik.touched["internetBill"] && (
          <Typography textAlign={"left"} variant="body2" color="red">
            {formik.errors["internetBill"]}
          </Typography>
        )}
      </CreateProfileCardWrapper>
      <div className="row if-no-select-show-screen mb-bottom-24">
        <div className="col-12 col-sm-12 col-md-8 col-lg-8 col-xl-9">
          <h5 className="mb-0 w-auto">
            {userData?.livingInCanada === "yes"
              ? t("expenses.resources.11")
              : "Here is a resource to help you compare internet plans so you can make sure you’ll get the most out of your money!"}
          </h5>
        </div>
        <div className="col-12 col-sm-12 col-md-4 col-lg-4 col-xl-3">
          <Button
            className="no-employee-btn"
            onClick={() => window.open("https://www.planhub.ca/home-internet")}
          >
            {t("newTab")}
          </Button>
        </div>
      </div>
      <CreateProfileCardWrapper>
        <h5 className="d-flex align-items-center justify-content-between">
          {t("expenses.ques14.title")}
          <Button
            className="border-0 bg-tranaparent w-auto h-auto shadow-none p-0 info-icon-btn"
            onClick={() => {
              dispatch(setInfoContent(t("expenses.resources.12")));
            }}
          >
            <img
              src="/assets/images/icons/info-circle-icon.svg"
              alt=""
              className="img-fluid"
            />
          </Button>
        </h5>
        <p>{t("expenses.ques14.helperText")}</p>
        <Input
          prefix={formik.values.subscriptions ? "$" : " "}
          placeholder={t("expenses.commonPlaceholder")}
          name={`subscriptions`}
          onChange={formik.handleChange}
          onKeyDown={handleKeyDown}
          value={formatNumber(formik.values.subscriptions)}
          suffix={
            formik.values.subscriptions ? (
              <img
                src="/assets/images/icons/primary-check-icon.svg"
                alt=""
                className=""
              />
            ) : (
              ""
            )
          }
          className={getErrorDataClassName("subscriptions", formik)}
        />
        {formik.errors["subscriptions"] && formik.touched["subscriptions"] && (
          <Typography textAlign={"left"} variant="body2" color="red">
            {formik.errors["subscriptions"]}
          </Typography>
        )}
      </CreateProfileCardWrapper>
      {userData?.livingInCanada !== "yes" && (
        <div className="row if-no-select-show-screen mb-bottom-24">
          <div className="col-12 col-sm-12 col-md-8 col-lg-8 col-xl-9">
            <h5 className="mb-0 w-auto">{t("expenses.resources.12")}</h5>
          </div>
        </div>
      )}
      <CreateProfileCardWrapper>
        <h5 className="d-flex align-items-center">
          {t("expenses.ques15.title")}
        </h5>
        <Input
          prefix={formik.values.groceries ? "$" : " "}
          placeholder={t("expenses.commonPlaceholder")}
          name={"groceries"}
          onChange={formik.handleChange}
          onKeyDown={handleKeyDown}
          value={formatNumber(formik.values.groceries)}
          suffix={
            formik.values.groceries ? (
              <img
                src="/assets/images/icons/primary-check-icon.svg"
                alt=""
                className=""
              />
            ) : (
              ""
            )
          }
          className={getErrorDataClassName("groceries", formik)}
        />
        {avgVaues?.groceries && (
          <div className="avg-value-div d-flex  align-items-center justify-content-between w-100">
            <div className="avg-value-text">
              {t("expenses.resources.13", {
                value1: userData?.city || userData?.cityNew,
                value2: getHouseHoldCount() || 1,
                value3: avgVaues?.groceries,
              })}
            </div>
            <Button
              className="avg-value-btn"
              onClick={() =>
                formik.setFieldValue("groceries", avgVaues?.groceries || 0)
              }
            >
              {t("avgBtn")}
            </Button>
          </div>
        )}
        {formik.errors["groceries"] && formik.touched["groceries"] && (
          <Typography textAlign={"left"} variant="body2" color="red">
            {formik.errors["groceries"]}
          </Typography>
        )}
      </CreateProfileCardWrapper>
      <div className="row if-no-select-show-screen mb-bottom-24">
        <div className="col-12 col-sm-12 col-md-8 col-lg-8 col-xl-9">
          <h5 className="mb-0 w-auto">{t("expenses.resources.14")}</h5>
        </div>
        <div className="col-12 col-sm-12 col-md-4 col-lg-4 col-xl-3">
          <Button
            className="no-employee-btn"
            onClick={() =>
              window.open(
                "https://www.creditcanada.com/blog/how-to-save-money-on-groceries"
              )
            }
          >
            {t("newTab")}
          </Button>
        </div>
      </div>
      <CreateProfileCardWrapper>
        <h5>{t("expenses.ques16.title")}</h5>
        <p>{t("expenses.ques16.helperText")}</p>
        <Input
          prefix={formik.values.eatingOut ? "$" : " "}
          placeholder={t("expenses.commonPlaceholder")}
          name={`eatingOut`}
          onChange={formik.handleChange}
          onKeyDown={handleKeyDown}
          value={formatNumber(formik.values.eatingOut)}
          suffix={
            formik.values.eatingOut ? (
              <img
                src="/assets/images/icons/primary-check-icon.svg"
                alt=""
                className=""
              />
            ) : (
              ""
            )
          }
          className={getErrorDataClassName("eatingOut", formik)}
        />
        {formik.errors["eatingOut"] && formik.touched["eatingOut"] && (
          <Typography textAlign={"left"} variant="body2" color="red">
            {formik.errors["eatingOut"]}
          </Typography>
        )}
      </CreateProfileCardWrapper>
    </div>
  );
};
const HomeMaintenance = ({ formik, handleKeyPress }) => {
  const userData = useSelector((state) => state.form.userData);
  const { t } = useTranslation();
  return (
    <div className="row mx-0 profile-second-card-row">
      <h5>
        {" "}
        {userData?.livingInCanada === "yes"
          ? t("expenses.ques5.title")
          : "How much do you think you’ll spend on the following expenses each month?"}
      </h5>
      <CreateProfileCardWrapper>
        <h5 className="d-flex align-items-center justify-content-between">
          {t("expenses.ques6.title")}
        </h5>
        <Input
          prefix={formik.values.homeInsurance ? "$" : " "}
          placeholder={t("expenses.commonPlaceholder")}
          name={`homeInsurance`}
          onChange={formik.handleChange}
          onKeyDown={handleKeyDown}
          value={formatNumber(formik.values.homeInsurance)}
          suffix={
            formik.values.condoFee ? (
              <img
                src="/assets/images/icons/primary-check-icon.svg"
                alt=""
                className=""
              />
            ) : (
              ""
            )
          }
          className={getErrorDataClassName("homeInsurance", formik)}
        />
        <div className="avg-value-div d-flex  align-items-center justify-content-between w-100">
          <div className="avg-value-text">{t("expenses.resources.28")}</div>
          <Button
            className="avg-value-btn"
            onClick={() => formik.setFieldValue("homeInsurance", 80)}
          >
            {t("avgBtn")}
          </Button>
        </div>
        {formik.errors["homeInsurance"] && formik.touched["homeInsurance"] && (
          <Typography textAlign={"left"} variant="body2" color="red">
            {formik.errors["homeInsurance"]}
          </Typography>
        )}
      </CreateProfileCardWrapper>
      <CreateProfileCardWrapper>
        <h5 className="d-flex align-items-center justify-content-between">
          {t("expenses.ques7.title")}
        </h5>
        <Input
          prefix={formik.values.condoFee ? "$" : " "}
          placeholder={t("expenses.commonPlaceholder")}
          name={`condoFee`}
          onChange={formik.handleChange}
          onKeyDown={handleKeyDown}
          value={formatNumber(formik.values.condoFee)}
          suffix={
            formik.values.condoFee ? (
              <img
                src="/assets/images/icons/primary-check-icon.svg"
                alt=""
                className=""
              />
            ) : (
              ""
            )
          }
          className={getErrorDataClassName("condoFee", formik)}
        />
        <div className="avg-value-div d-flex  align-items-center justify-content-between w-100">
          <div className="avg-value-text">{t("expenses.ques7.helperText")}</div>
        </div>
        {formik.errors["condoFee"] && formik.touched["condoFee"] && (
          <Typography textAlign={"left"} variant="body2" color="red">
            {formik.errors["condoFee"]}
          </Typography>
        )}
      </CreateProfileCardWrapper>
      <CreateProfileCardWrapper>
        <h5 className="d-flex align-items-center justify-content-between">
          {t("expenses.ques8.title")}
        </h5>
        <Input
          prefix={formik.values.taxPay ? "$" : " "}
          placeholder={t("expenses.commonPlaceholder")}
          name={`taxPay`}
          onChange={formik.handleChange}
          onKeyDown={handleKeyDown}
          value={formatNumber(formik.values.taxPay)}
          suffix={
            formik.values.taxPay ? (
              <img
                src="/assets/images/icons/primary-check-icon.svg"
                alt=""
                className=""
              />
            ) : (
              ""
            )
          }
          className={getErrorDataClassName("taxPay", formik)}
        />

        {formik.errors["taxPay"] && formik.touched["taxPay"] && (
          <Typography textAlign={"left"} variant="body2" color="red">
            {formik.errors["taxPay"]}
          </Typography>
        )}
      </CreateProfileCardWrapper>
      <CreateProfileCardWrapper>
        <h5 className="d-flex align-items-center">
          {t("expenses.ques9.title")}
        </h5>
        <p>{t("expenses.ques9.helperText1")} </p>
        <Input
          prefix={formik.values.homeMaintenance ? "$" : " "}
          placeholder={t("expenses.commonPlaceholder")}
          name={"homeMaintenance"}
          onChange={formik.handleChange}
          onKeyDown={handleKeyDown}
          value={formatNumber(formik.values.homeMaintenance)}
          suffix={
            formik.values.homeMaintenance ? (
              <img
                src="/assets/images/icons/primary-check-icon.svg"
                alt=""
                className=""
              />
            ) : (
              ""
            )
          }
          className={getErrorDataClassName("homeMaintenance", formik)}
        />
        <div className="avg-value-div d-flex  align-items-center justify-content-between w-100">
          <div className="avg-value-text">
            {t("expenses.ques9.helperText2")}
          </div>
        </div>
        {formik.errors["homeMaintenance"] &&
          formik.touched["homeMaintenance"] && (
            <Typography textAlign={"left"} variant="body2" color="red">
              {formik.errors["homeMaintenance"]}
            </Typography>
          )}
      </CreateProfileCardWrapper>
    </div>
  );
};
const RentInsurance = ({ formik, handleKeyPress }) => {
  const { fetchAvgVal } = useAvgValue();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const userData = useSelector((state) => state.form.userData);
  const [avgValue, setAvgValue] = useState(0);
  useEffect(() => {
    handleFetchAvg("/city_cost_estimator", "rentAmount");
  }, []);
  const handleFetchAvg = async (endpoint, fieldName) => {
    const updatedVal = await fetchAvgVal(endpoint, "Rent");
    if (updatedVal) {
      setAvgValue(Math.round(updatedVal?.estimate));
    }
  };

  const getTotalMembers = () => {
    const foundChildren =
      (userData?.houseHold?.length > 0 &&
        userData?.houseHold?.find((i) => i.type === "children")?.count) ||
      0;
    const foundMembers =
      (userData?.houseHold?.length > 0 &&
        userData?.houseHold?.find((i) => i.type === "otherMembers")?.count) ||
      0;
    const isMyself =
      userData?.houseHold?.length > 0 &&
      userData?.houseHold?.find((i) => i?.type === "myself");
    const isPartner =
      userData?.houseHold?.length > 0 &&
      userData?.houseHold?.find((i) => i?.type === "partner");
    let totalMembers = Number(foundMembers) + Number(foundChildren);
    if (isMyself) {
      totalMembers = Number(totalMembers) + 1;
    }
    if (isPartner) {
      totalMembers = Number(totalMembers) + 1;
    }
    return totalMembers;
  };
  return (
    <>
      <CreateProfileCardWrapper>
        <h5 className="d-flex align-items-center">
          {userData?.livingInCanada === "yes"
            ? t("expenses.ques2.title")
            : "How much do you expect to pay monthly?"}
        </h5>
        <Input
          prefix={formik.values.rentAmount ? "$" : " "}
          placeholder={t("expenses.commonPlaceholder")}
          name={"rentAmount"}
          onChange={formik.handleChange}
          onKeyDown={handleKeyDown}
          value={formatNumber(formik.values.rentAmount)}
          suffix={
            formik.values.rentAmount ? (
              <img
                src="/assets/images/icons/primary-check-icon.svg"
                alt=""
                className=""
              />
            ) : (
              ""
            )
          }
          className={getErrorDataClassName("rentAmount", formik)}
        />
        {avgValue ? (
          <div className="avg-value-div d-flex  align-items-center justify-content-between w-100">
            <div className="avg-value-text">
              {t("expenses.resources.2", {
                value1: getTotalMembers() > 2 ? 3 : 1,
                value2: userData?.city || userData?.cityNew,
                value3: avgValue,
              })}
            </div>
            <Button
              className="avg-value-btn"
              onClick={() => formik.setFieldValue("rentAmount", `${avgValue}`)}
            >
              {t("avgBtn")}
            </Button>
          </div>
        ) : (
          ""
        )}
        {formik.errors["rentAmount"] && formik.touched["rentAmount"] && (
          <Typography textAlign={"left"} variant="body2" color="red">
            {formik.errors["rentAmount"]}
          </Typography>
        )}
      </CreateProfileCardWrapper>
      <CreateProfileCardWrapper>
        <h5 className="d-flex align-items-center justify-content-between">
          {userData?.livingInCanada === "yes"
            ? t("expenses.ques3.title")
            : "How much do you think you’ll pay for tenant insurance?"}
          <Button
            className="border-0 bg-tranaparent w-auto h-auto shadow-none p-0 info-icon-btn"
            onClick={() => {
              dispatch(setInfoContent(t("expenses.resources.4")));
            }}
          >
            <img
              src="/assets/images/icons/info-circle-icon.svg"
              alt=""
              className="img-fluid"
            />
          </Button>
        </h5>
        <p>{t("expenses.ques3.helperText")} </p>
        <Input
          prefix={formik.values.tenantInsurance ? "$" : " "}
          placeholder={t("expenses.commonPlaceholder")}
          name={`tenantInsurance`}
          onChange={formik.handleChange}
          onKeyDown={handleKeyDown}
          value={formatNumber(formik.values.tenantInsurance)}
          suffix={
            formik.values.tenantInsurance ? (
              <img
                src="/assets/images/icons/primary-check-icon.svg"
                alt=""
                className=""
              />
            ) : (
              ""
            )
          }
          className={getErrorDataClassName("tenantInsurance", formik)}
        />
        <div className="avg-value-div d-flex  align-items-center justify-content-between w-100">
          <div className="avg-value-text">{t("expenses.resources.3")}</div>
          <Button
            className="avg-value-btn"
            onClick={() => formik.setFieldValue("tenantInsurance", 30)}
          >
            {t("avgBtn")}
          </Button>
        </div>
        {formik.errors["tenantInsurance"] &&
          formik.touched["tenantInsurance"] && (
            <Typography textAlign={"left"} variant="body2" color="red">
              {formik.errors["tenantInsurance"]}
            </Typography>
          )}
      </CreateProfileCardWrapper>
    </>
  );
};
const MoneySend = ({ formik, handleKeyPress }) => {
  const { t } = useTranslation();
  return (
    <>
      <CreateProfileCardWrapper>
        <h5>{t("expenses.ques17.title")}</h5>
        <Radio.Group
          className="w-100"
          defaultValue={1}
          name="supportFamily"
          value={formik.values?.supportFamily}
          onChange={(e) => {
            if (e.target.value === "no") {
              formik.setFieldValue("supportAmount", "");
            }
            formik.handleChange(e);
          }}
        >
          <ul className="list-inline mb-0">
            <li>
              <Radio value={"yes"}>{t("expenses.ques17.options.1")}</Radio>
            </li>
            <li>
              <Radio value={"no"}>{t("expenses.ques17.options.2")}</Radio>
            </li>
          </ul>
        </Radio.Group>
        {formik.errors["supportFamily"] && formik.touched["supportFamily"] && (
          <Typography textAlign={"left"} variant="body2" color="red">
            {formik.errors["supportFamily"]}
          </Typography>
        )}
      </CreateProfileCardWrapper>
      {formik.values?.supportFamily === "yes" && (
        <div className="row if-no-select-show-screen mb-bottom-24">
          <div className="col-12 col-sm-12 col-md-8 col-lg-8 col-xl-9">
            <h5 className="mb-0 w-auto">{t("expenses.resources.15")}</h5>
          </div>
          <div className="col-12 col-sm-12 col-md-4 col-lg-4 col-xl-3">
            <Button
              className="no-employee-btn"
              onClick={() =>
                window.open(
                  "https://canadianimmigrant.ca/money-and-business/how-to-save-fees-on-sending-money-overseas"
                )
              }
            >
              {t("newTab")}
            </Button>
          </div>
        </div>
      )}
      {formik.values.supportFamily === "yes" && (
        <CreateProfileCardWrapper>
          <h5 className="d-flex align-items-center">
            {t("expenses.ques17.child")}
          </h5>
          <Input
            prefix={formik.values.supportAmount ? "$" : " "}
            placeholder={t("expenses.commonPlaceholder")}
            name={`supportAmount`}
            onChange={formik.handleChange}
            onKeyDown={handleKeyDown}
            value={formatNumber(formik.values.supportAmount)}
            suffix={
              formik.values.supportAmount ? (
                <img
                  src="/assets/images/icons/primary-check-icon.svg"
                  alt=""
                  className=""
                />
              ) : (
                ""
              )
            }
            className={getErrorDataClassName("supportAmount", formik)}
          />

          {formik.errors["supportAmount"] &&
            formik.touched["supportAmount"] && (
              <Typography textAlign={"left"} variant="body2" color="red">
                {formik.errors["supportAmount"]}
              </Typography>
            )}
        </CreateProfileCardWrapper>
      )}
    </>
  );
};
const PersonalVehicles = ({ formik }) => {
  const userData = useSelector((state) => state.form.userData);
  const { t } = useTranslation();
  return (
    <>
      <CreateProfileCardWrapper>
        <h5>
          {userData?.livingInCanada === "yes"
            ? t("expenses.ques18.title")
            : "Let’s talk about transportation. Will you have a vehicle in Canada?"}
        </h5>
        <Radio.Group
          className="w-100"
          defaultValue={1}
          name="carOwn"
          value={formik.values?.carOwn}
          onChange={(e) => {
            if (e.target.value === "no") {
              formik.setValues({
                ...formik.values,
                carGasCost: "",
                carInsurance: "",
                carMaintenance: "",
                carPayment: "",
              });
            }
            formik.handleChange(e);
          }}
        >
          <ul className="list-inline mb-0">
            <li>
              <Radio value={"yes"}>{t("expenses.ques18.options.1")}</Radio>
            </li>
            <li>
              <Radio value={"no"}>{t("expenses.ques18.options.2")}</Radio>
            </li>
          </ul>
        </Radio.Group>
        {formik.errors["carOwn"] && formik.touched["carOwn"] && (
          <Typography textAlign={"left"} variant="body2" color="red">
            {formik.errors["carOwn"]}
          </Typography>
        )}
      </CreateProfileCardWrapper>
      {formik.values?.carOwn === "yes" && (
        <CreateProfileCardWrapper>
          <h5 className="d-flex align-items-center justify-content-between">
            {userData?.livingInCanada === "yes"
              ? t("expenses.ques19.title")
              : "Which of the below would apply to you?"}
          </h5>
          <Radio.Group
            className="w-100"
            defaultValue={1}
            name="carStatus"
            value={formik.values?.carStatus}
            onChange={(e) => {
              if (e.target.value != "nothing") {
                formik.setFieldValue("carPayment", "");
              }
              formik.handleChange(e);
            }}
          >
            <ul className="list-inline mb-0">
              <li>
                <Radio value={"finance"}>
                  {userData?.livingInCanada === "yes"
                    ? t("expenses.ques19.options.1")
                    : "I will finance a vehicle"}
                </Radio>
              </li>
              <li>
                <Radio value={"lease"}>
                  {userData?.livingInCanada === "yes"
                    ? t("expenses.ques19.options.2")
                    : "I will lease a vehicle"}
                </Radio>
              </li>
              <li>
                <Radio value={"nothing"}>
                  {userData?.livingInCanada === "yes"
                    ? t("expenses.ques19.options.3")
                    : "I will own a vehicle with no payments"}
                </Radio>
              </li>
            </ul>
          </Radio.Group>
          {formik.errors["carStatus"] && formik.touched["carStatus"] && (
            <Typography textAlign={"left"} variant="body2" color="red">
              {formik.errors["carStatus"]}
            </Typography>
          )}
        </CreateProfileCardWrapper>
      )}
    </>
  );
};
const MoneyMaking = ({ formik, handleKeyPress }) => {
  const userData = useSelector((state) => state.form.userData);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  return (
    <CreateProfileCardWrapper>
      <h5 className="d-flex align-items-center justify-content-between">
        {userData?.livingInCanada === "yes"
          ? t("expenses.ques20.title")
          : "How much do you plan to pay each month for your vehicle?"}
        {userData?.livingInCanada !== "yes" && (
          <Button
            className="border-0 bg-tranaparent w-auto h-auto shadow-none p-0 info-icon-btn"
            onClick={() => {
              dispatch(
                setInfoContent(
                  "The cost of a vehicle can vary drastically depending on the type, age, and condition of the car. For example, a new vehicle will cost more than an older, used one. Keep this in mind when car shopping!"
                )
              );
            }}
          >
            <img
              src="/assets/images/icons/info-circle-icon.svg"
              alt=""
              className="img-fluid"
            />
          </Button>
        )}
      </h5>
      <Input
        style={{
          marginTop: "15px",
        }}
        prefix={formik.values.carPayment ? "$" : " "}
        placeholder={t("expenses.commonPlaceholder")}
        name="carPayment"
        onChange={formik.handleChange}
        onKeyDown={handleKeyDown}
        value={formatNumber(formik.values.carPayment)}
        suffix={
          formik.values.carPayment ? (
            <img
              src="/assets/images/icons/primary-check-icon.svg"
              alt=""
              className=""
            />
          ) : (
            ""
          )
        }
        className={getErrorDataClassName("carPayment", formik)}
      />
      {formik.errors["carPayment"] && formik.touched["carPayment"] && (
        <Typography textAlign={"left"} variant="body2" color="red">
          {formik.errors["carPayment"]}
        </Typography>
      )}
    </CreateProfileCardWrapper>
  );
};
const PayInsuranceMoney = ({ formik, handleKeyPress }) => {
  const userData = useSelector((state) => state.form.userData);
  const [avgValue, setAvgValue] = useState("");
  const { t } = useTranslation();
  useEffect(() => {
    if (userData?.province) {
      handleFetchAvg();
    }
  }, [userData?.province]);
  const handleFetchAvg = () => {
    const { province } = userData;
    if (province === "Alberta") {
      setAvgValue(1316);
    } else if (province === "British Columbia") {
      setAvgValue(1832);
    } else if (province === "Manitoba") {
      setAvgValue(1140);
    } else if (province === "New Brunswick") {
      setAvgValue(876);
    } else if (province === "Newfoundland and Labrador") {
      setAvgValue(1168);
    } else if (province === "Nova Scotia") {
      setAvgValue(891);
    } else if (province === "Nunavut") {
      setAvgValue(963);
    } else if (province === "Ontario") {
      setAvgValue(1528);
    } else if (province === "Prince Edward Island") {
      setAvgValue(861);
    } else if (province === "Quebec") {
      setAvgValue(717);
    } else if (province === "Northwest Territories") {
      setAvgValue(978);
    } else if (province === "Yukon") {
      setAvgValue(812);
    } else if (province === "Saskatchewan") {
      setAvgValue(1233);
    } else {
      setAvgValue(0);
    }
  };
  return (
    <>
      <CreateProfileCardWrapper>
        <h5 className="d-flex align-items-center justify-content-between">
          {userData?.livingInCanada === "yes"
            ? t("expenses.ques21.title")
            : "How much do you think you’ll pay for car insurance?"}
        </h5>
        <Input
          style={{
            marginTop: "15px",
          }}
          prefix={formik.values.carInsurancePayment ? "$" : " "}
          placeholder={t("expenses.commonPlaceholder")}
          name="carInsurancePayment"
          onChange={formik.handleChange}
          onKeyDown={handleKeyDown}
          value={formatNumber(formik.values.carInsurancePayment)}
          suffix={
            formik.values.carInsurancePayment ? (
              <img
                src="/assets/images/icons/primary-check-icon.svg"
                alt=""
                className=""
              />
            ) : (
              ""
            )
          }
          className={getErrorDataClassName("carInsurancePayment", formik)}
        />
        <div className="avg-value-div d-flex  align-items-center justify-content-between w-100">
          <div className="avg-value-text">
            {t("expenses.resources.16", {
              value1: userData?.province || userData?.provinceNew,
              value2: Math.round(Number(avgValue)),
              value3: Math.round(avgValue / 12),
            })}
          </div>
          <Button
            className="avg-value-btn"
            onClick={() =>
              formik.setFieldValue(
                "carInsurancePayment",
                `${Math.round(avgValue / 12)}`
              )
            }
          >
            {t("avgBtn")}
          </Button>
        </div>
        {formik.errors["carInsurancePayment"] &&
          formik.touched["carInsurancePayment"] && (
            <Typography textAlign={"left"} variant="body2" color="red">
              {formik.errors["carInsurancePayment"]}
            </Typography>
          )}
      </CreateProfileCardWrapper>
      <div className="row if-no-select-show-screen mb-bottom-24">
        <div className="col-12 col-sm-12 col-md-8 col-lg-8 col-xl-9">
          <h5 className="mb-0 w-auto">{t("expenses.resources.17")}</h5>
        </div>
        <div className="col-12 col-sm-12 col-md-4 col-lg-4 col-xl-3">
          <Button
            className="no-employee-btn"
            onClick={() =>
              window.open(
                "https://www.ratehub.ca/insurance/best-car-insurance-quote"
              )
            }
          >
            {t("newTab")}
          </Button>
        </div>
      </div>
    </>
  );
};
const AdditionalExpensesMaintenanceGas = ({ formik, handleKeyPress }) => {
  const userData = useSelector((state) => state.form.userData);
  const { t } = useTranslation();
  return (
    <div className="row mx-0 profile-second-card-row">
      <div className="col-12">
        <div className="row">
          <div className="col-12 profile-card-inner">
            <p> {t("expenses.ques22.helperText")}</p>
            <h5
              style={{ width: "95%" }}
              className="d-flex align-items-center justify-content-between"
            >
              {userData?.livingInCanada === "yes"
                ? t("expenses.ques22.title")
                : "How much money do you think you’ll spend each month on gas and maintenance for your vehicle?"}
            </h5>
          </div>
        </div>
        <div className="row">
          <div className="col-12">
            <CreateProfileCardWrapper collapseInnerCard={true}>
              <h5 className="d-flex align-items-center">
                {t("expenses.ques23.title")}
              </h5>
              <Input
                prefix={formik.values.carGasCost ? "$" : " "}
                placeholder={t("expenses.commonPlaceholder")}
                name="carGasCost"
                onChange={formik.handleChange}
                onKeyDown={handleKeyDown}
                value={formatNumber(formik.values.carGasCost)}
                suffix={
                  formik.values.carGasCost ? (
                    <img
                      src="/assets/images/icons/primary-check-icon.svg"
                      alt=""
                      className=""
                    />
                  ) : (
                    ""
                  )
                }
                className={getErrorDataClassName("carGasCost", formik)}
              />
              <div className="avg-value-div d-flex  align-items-center justify-content-between w-100">
                <div className="avg-value-text">
                  {t("expenses.resources.18")}
                </div>
                <Button
                  className="avg-value-btn"
                  onClick={() => formik.setFieldValue("carGasCost", 200)}
                >
                  {t("avgBtn")}
                </Button>
              </div>
              {formik.errors["carGasCost"] && (
                <Typography textAlign={"left"} variant="body2" color="red">
                  {formik.errors["carGasCost"]}
                </Typography>
              )}
            </CreateProfileCardWrapper>
            <CreateProfileCardWrapper collapseInnerCard={true}>
              <h5 className="d-flex align-items-center justify-content-between">
                {t("expenses.ques24.title")}
              </h5>
              <p> {t("expenses.ques24.helperText")}</p>
              <Input
                prefix={formik.values.carMaintenance ? "$" : " "}
                placeholder={t("expenses.commonPlaceholder")}
                name="carMaintenance"
                onChange={formik.handleChange}
                onKeyDown={handleKeyDown}
                value={formatNumber(formik.values.carMaintenance)}
                suffix={
                  formik.values.carMaintenance ? (
                    <img
                      src="/assets/images/icons/primary-check-icon.svg"
                      alt=""
                      className=""
                    />
                  ) : (
                    ""
                  )
                }
                className={getErrorDataClassName("carMaintenance", formik)}
              />
              <div className="avg-value-div d-flex  align-items-center justify-content-between w-100">
                <div className="avg-value-text">
                  {t("expenses.resources.19")}
                </div>
                <Button
                  className="avg-value-btn"
                  onClick={() => formik.setFieldValue("carMaintenance", 79)}
                >
                  {t("avgBtn")}
                </Button>
              </div>
              {formik.errors["carMaintenance"] && (
                <Typography textAlign={"left"} variant="body2" color="red">
                  {formik.errors["carMaintenance"]}
                </Typography>
              )}
            </CreateProfileCardWrapper>
          </div>
        </div>
      </div>
    </div>
  );
};
const SpendTransportationExpenses = ({ formik, handleKeyPress }) => {
  const userData = useSelector((state) => state.form.userData);
  const [avgValue, setAvgValue] = useState(0);
  const { fetchAvgVal } = useAvgValue();
  const { t } = useTranslation();
  useEffect(() => {
    handleFetchAvg("/city_cost_estimator");
  }, []);
  const handleFetchAvg = async (endpoint) => {
    const updatedVal = await fetchAvgVal(
      endpoint,
      "Public Transport and Taxi",
      true
    );
    setAvgValue(Math.round(updatedVal?.estimate) || 0);
  };

  return (
    <>
      <CreateProfileCardWrapper>
        <h5 className="d-flex align-items-center justify-content-between">
          {userData?.livingInCanada === "yes"
            ? t("expenses.ques25.title")
            : "How much do you think you’ll spend on transportation each month?"}
        </h5>
        <p>{t("expenses.ques25.helperText")}</p>
        <Input
          prefix={formik.values.transportationPay ? "$" : " "}
          placeholder={t("expenses.commonPlaceholder")}
          name="transportationPay"
          onChange={formik.handleChange}
          onKeyDown={handleKeyDown}
          value={formatNumber(formik.values.transportationPay)}
          suffix={
            formik.values.transportationPay ? (
              <img
                src="/assets/images/icons/primary-check-icon.svg"
                alt=""
                className=""
              />
            ) : (
              ""
            )
          }
          className={getErrorDataClassName("transportationPay", formik)}
        />
        {avgValue ? (
          <div className="avg-value-div d-flex  align-items-center justify-content-between w-100">
            <div className="avg-value-text">
              {t("expenses.resources.20", {
                value1: userData?.city || userData?.cityNew,
                value2: avgValue,
              })}
            </div>
            <Button
              className="avg-value-btn"
              onClick={() =>
                formik.setFieldValue("transportationPay", `${avgValue}`)
              }
            >
              {t("avgBtn")}
            </Button>
          </div>
        ) : (
          ""
        )}
        {formik.errors["transportationPay"] &&
          formik.touched["transportationPay"] && (
            <Typography textAlign={"left"} variant="body2" color="red">
              {formik.errors["transportationPay"]}
            </Typography>
          )}
      </CreateProfileCardWrapper>
    </>
  );
};
const SpendClothsGiftsExpenses = ({ formik, handleKeyPress }) => {
  const userData = useSelector((state) => state.form.userData);
  const { t } = useTranslation();
  return (
    <div className="row mx-0 profile-second-card-row">
      <div className="col-12">
        <div className="row">
          <div className="col-12 profile-card-inner">
            <h5>
              {userData?.livingInCanada === "yes"
                ? t("expenses.ques26.title")
                : "How much do you think you’ll spend on other items, such as clothes and gifts, per month?"}
            </h5>
          </div>
        </div>
        <div className="row">
          <div className="col-12">
            <CreateProfileCardWrapper collapseInnerCard={true}>
              <h5 className="d-flex align-items-center">
                {t("expenses.ques27.title")}
              </h5>
              <Input
                prefix={formik.values.clothingExpenses ? "$" : " "}
                placeholder={t("expenses.commonPlaceholder")}
                name={"clothingExpenses"}
                onChange={formik.handleChange}
                onKeyDown={handleKeyDown}
                value={formatNumber(formik.values.clothingExpenses)}
                suffix={
                  formik.values.clothingExpenses ? (
                    <img
                      src="/assets/images/icons/primary-check-icon.svg"
                      alt=""
                      className=""
                    />
                  ) : (
                    ""
                  )
                }
                className={getErrorDataClassName("clothingExpenses", formik)}
              />
              <div className="avg-value-div d-flex  align-items-center justify-content-between w-100">
                <div className="avg-value-text">
                  {t("expenses.resources.21")}
                </div>
                <Button
                  className="avg-value-btn"
                  onClick={() =>
                    formik.setFieldValue("clothingExpenses", `190`)
                  }
                >
                  {t("avgBtn")}
                </Button>
              </div>
              {formik.errors["clothingExpenses"] && (
                <Typography textAlign={"left"} variant="body2" color="red">
                  {formik.errors["clothingExpenses"]}
                </Typography>
              )}
            </CreateProfileCardWrapper>
            <div className="row if-no-select-show-screen three-line mb-bottom-24">
              <div className="col-12 col-sm-12 col-md-8 col-lg-8 col-xl-9">
                <h5 className="mb-0 w-auto">{t("expenses.resources.22")}</h5>
              </div>
              <div className="col-12 col-sm-12 col-md-4 col-lg-4 col-xl-3">
                <Button
                  onClick={() => {
                    window.open("https://www.valuevillage.ca");
                  }}
                  className="no-employee-btn"
                >
                  {t("newTab")}
                </Button>
              </div>
            </div>
            <CreateProfileCardWrapper collapseInnerCard={true}>
              <h5 className="d-flex align-items-center">
                {t("expenses.ques28.title")}
              </h5>
              <Input
                suffix={
                  formik.values.footwearExpenses ? (
                    <img
                      src="/assets/images/icons/primary-check-icon.svg"
                      alt=""
                      className=""
                    />
                  ) : (
                    ""
                  )
                }
                prefix={formik.values.footwearExpenses ? "$" : " "}
                placeholder={t("expenses.commonPlaceholder")}
                name={"footwearExpenses"}
                onKeyDown={handleKeyDown}
                value={formatNumber(formik.values.footwearExpenses)}
                onChange={formik.handleChange}
                className={getErrorDataClassName("footwearExpenses", formik)}
              />
              <div className="avg-value-div d-flex  align-items-center justify-content-between w-100">
                <div className="avg-value-text">
                  {t("expenses.ques28.helperText")}
                </div>
              </div>
              {formik.errors["footwearExpenses"] && (
                <Typography textAlign={"left"} variant="body2" color="red">
                  {formik.errors["footwearExpenses"]}
                </Typography>
              )}
            </CreateProfileCardWrapper>
            <CreateProfileCardWrapper collapseInnerCard={true}>
              <h5 className="d-flex align-items-center">
                {t("expenses.ques29.title")}
              </h5>
              <Input
                prefix={formik.values.giftingExpenses ? "$" : " "}
                placeholder={t("expenses.commonPlaceholder")}
                name={"giftingExpenses"}
                suffix={
                  formik.values.giftingExpenses ? (
                    <img
                      src="/assets/images/icons/primary-check-icon.svg"
                      alt=""
                      className=""
                    />
                  ) : (
                    ""
                  )
                }
                onKeyDown={handleKeyDown}
                value={formatNumber(formik.values.giftingExpenses)}
                onChange={formik.handleChange}
                className={getErrorDataClassName("giftingExpenses", formik)}
              />
              {formik.errors["giftingExpenses"] && (
                <Typography textAlign={"left"} variant="body2" color="red">
                  {formik.errors["giftingExpenses"]}
                </Typography>
              )}
            </CreateProfileCardWrapper>
            <div className="row if-no-select-show-screen three-line mb-bottom-24">
              <div className="col-12 col-sm-12 col-md-8 col-lg-8 col-xl-9">
                <h5 className="mb-0 w-auto">{t("expenses.resources.23")}</h5>
              </div>
              <div className="col-12 col-sm-12 col-md-4 col-lg-4 col-xl-3">
                <Button
                  onClick={() => {
                    window.open(
                      "https://www.readersdigest.ca/travel/canada/canadian-gift-guide/"
                    );
                  }}
                  className="no-employee-btn"
                >
                  {t("newTab")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
const AdditionalExpensesCaregories = ({
  formik,
  handleEditableStep,
  IS_REVIEW,
}) => {
  const [categories, setCategories] = useState([]);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const handleCheckChange = (type) => {
    const categories =
      formik.values.expenseCategories?.length > 0
        ? [...formik.values.expenseCategories]
        : [];
    const filteredCategories = categories.filter((i) => i.type != type);
    const arr = filteredCategories.map((i) => {
      return {
        type: i,
        amount: "",
      };
    });
    formik.setFieldValue("incomeArr", arr);
    formik.setFieldValue("expenseCategories", filteredCategories);
    if (IS_REVIEW) {
      handleEditableStep(14);
    }
  };

  useEffect(() => {
    getCategoriesList();
  }, []);
  const getCategoriesList = () => {
    dispatch(setLoading(true));
    getCategories("expenses")
      .then((resp) => {
        setCategories(resp.data.category);
      })
      .catch((Err) => {
        message.error(Err.message[i18n.language]);
      })
      .finally(() => {
        dispatch(setLoading(false));
      });
  };
  return (
    <CreateProfileCardWrapper>
      <h5 className="d-flex align-items-center justify-content-between">
        {t("expenses.ques30.title")}
      </h5>
      <p>{t("expenses.ques30.helperText")}</p>
      <Checkbox.Group
        className="custom-checkbox-select-button"
        name={"expenseCategories"}
        style={{ width: "100%" }}
        onChange={(e) => {
          let incomeArr = [...formik.values?.incomeArr];
          formik.setFieldValue("expenseCategories", e);
          const initArr = categories
            .map((item) => {
              return item.subTitle.filter((i) => e.some((n) => n === i.en));
            })
            .flat(1)
            .map((i) => i);
          const arr = initArr.map((i) => {
            return {
              type: i,
              amount:
                incomeArr?.find((n) => i.en === n.type?.en || i.en === n.type)
                  ?.amount || "",
            };
          });
          formik.setFieldValue("incomeArr", arr);
        }}
        value={formik.values?.expenseCategories}
      >
        <ul className="list-inline mb-0 additional-income-list">
          {categories.map((item) => {
            return (
              <>
                <h6 className="my-3">{item?.title[i18n.language]}</h6>
                {item.subTitle.map((innerItem) => {
                  return (
                    <li className="list-inline-item" key={innerItem?.en}>
                      <Checkbox
                        className="additional-income-btn"
                        value={innerItem?.en}
                        onClick={() => handleCheckChange(innerItem?.en)}
                      >
                        {innerItem[i18n.language]}
                        <img
                          src="/assets/images/icons/close-icon-white-small.svg"
                          alt=""
                          className="img-fluid close-icon-img"
                        />
                      </Checkbox>
                    </li>
                  );
                })}
              </>
            );
          })}
        </ul>
      </Checkbox.Group>
    </CreateProfileCardWrapper>
  );
};
const AdditionalExpenses = ({
  formik,
  editableStep,
  handleEditableStep,
  innerStepper,
  IS_REVIEW,
  handleKeyPress,
  setInnerStepper,
}) => {
  const userData = useSelector((state) => state.form.userData);
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const handleRemoveCategory = (item) => {
    const filteredCategories = formik.values.expenseCategories?.filter((i) => {
      
      return i != item.type?.en && i != item.type;
    });
    const filteredIncomeArr = formik.values.incomeArr?.filter(
      (i) => i.type != item.type
    );
    formik.setFieldValue("expenseCategories", filteredCategories);
    formik.setFieldValue("incomeArr", filteredIncomeArr);
  };
  const handleGetRange = () => {
    const province = userData?.province || userData?.provinceNew;
    if (province === "Alberta") {
      return "$900 and $1,400 ";
    } else if (province === "British Columbia") {
      return "$950 and $1,350";
    } else if (province === "Manitoba") {
      return "$450 and $650";
    } else if (province === "New Brunswick") {
      return "$690 and $860";
    } else if (province === "Newfoundland and Labrador") {
      return "$650 and $950";
    } else if (province === "Nova Scotia") {
      return "$870 and $990";
    } else if (province === "Nunavut") {
      return "$1,200 and $1,300";
    } else if (province === "Ontario") {
      return "$780 and $1,900";
    } else if (province === "Prince Edward Island") {
      return "$580 and $730";
    } else if (province === "Quebec") {
      return "$181";
    } else if (province === "Northwest Territories") {
      return "$830 and $1,015";
    } else if (province === "Saskatchewan") {
      return "$625 and $995";
    } else if (province === "Yukon") {
      return "$830 and $925";
    }
  };
  const handleAvgValue = (value, type) => {
    
    const selectedArr = [...formik.values.incomeArr];
    const foundIndex = selectedArr.findIndex(
      (item) => item.type?.en === type || item.type === type
    );
    selectedArr[foundIndex].amount = value;
    formik.setFieldValue("incomeArr", selectedArr);
  };

  useEffect(() => {
    getCategoriesList();
  }, [formik.values?.incomeArr?.length]);
  const getCategoriesList = () => {
    dispatch(setLoading(true));
    getCategories("expenses")
      .then((resp) => {
        if (
          resp.data.category?.length > 0 &&
          formik.values?.incomeArr?.length > 0
        ) {
          const incomeArr = [...formik.values?.incomeArr];
          let initArr = [];
          
          const filteredCategories = resp.data.category
            .map((item) => {
              return item.subTitle.filter((i) =>
                incomeArr.some((n) => n.type?.en === i.en || n.type === i.en)
              );
            })
            .flat(1)
            .map((i) => i);
          if (filteredCategories?.length > 0) {
            filteredCategories.forEach((i) => {
              initArr.push({
                type: i,
                amount:
                  incomeArr?.find((n) => i.en === n.type || i.en === n.type.en)
                    ?.amount || "",
              });
            });
          }
          formik.setFieldValue("incomeArr", initArr);
        }
      })
      .catch((Err) => {
        message.error(Err.message[i18n.language]);
      })
      .finally(() => {
        dispatch(setLoading(false));
      });
  };

  const getInfoComponent = (type) => {
    if (type === "Children's activities, camps and sports") {
      const content = t("expenses.additionalExpenses.12");
      const link =
        "https://activeforlife.com/how-do-parents-afford-childrens-extracurricular-activities/";
      return getRangeLinkContent(content, link);
    } else if (type === "Private medical insurance") {
      const content = t("expenses.additionalExpenses.5");
      const link = "https://www.ratehub.ca/blog/canada-health-insurance-cost/";
      return getRangeLinkContent(content, link);
    } else if (type === "Life insurance") {
      const content = t("expenses.additionalExpenses.10");
      const link =
        "https://www.policyme.com/blog/how-much-does-life-insurance-cost-in-canada";
      return getRangeLinkContent(content, link);
    } else if (type === "Daycare/Babysitters") {
      const { province, provinceNew } = userData;
      const range = handleGetRange();
      const content = t("expenses.additionalExpenses.11", {
        value1: province || provinceNew,
        value2: range,
      });
      const link =
        "https://arrivein.com/daily-life-in-canada/child-care-in-canada-types-cost-and-tips-for-newcomers/";
      return getRangeLinkContent(content, link);
    }
  };

  const getAvgRangeComponent = (type) => {
    if (type === "Pet food" || type === "Pet Food") {
      return (
        <div className="avg-value-div d-flex  align-items-center justify-content-between w-100">
          <div className="avg-value-text">
            {t("expenses.additionalExpenses.1")}
          </div>
        </div>
      );
    } else if (type === "Vet bills") {
      return (
        <div className="avg-value-div d-flex  align-items-center justify-content-between w-100">
          <div className="avg-value-text">
            {t("expenses.additionalExpenses.2")}
          </div>
        </div>
      );
    } else if (type === "Post-secondary education tuition") {
      return (
        <div className="avg-value-div d-flex  align-items-center justify-content-between w-100">
          <div className="avg-value-text">
            {t("expenses.additionalExpenses.17")}
          </div>
        </div>
      );
    } else if (type === "Alcohol/smoking etc.") {
      return (
        <div className="avg-value-div d-flex  align-items-center justify-content-between w-100">
          <div className="avg-value-text">
            {t("expenses.additionalExpenses.13")}
          </div>
          <Button
            className="avg-value-btn"
            onClick={() => handleAvgValue("68", type)}
          >
            {t("avgBtn")}
          </Button>
        </div>
      );
    } else if (type === "Post-secondary education expenses (textbooks, etc)") {
      return (
        <div className="avg-value-div d-flex  align-items-center justify-content-between w-100">
          <div className="avg-value-text">
            {t("expenses.additionalExpenses.18")}
          </div>
          <Button
            className="avg-value-btn"
            onClick={() => handleAvgValue("75", type)}
          >
            {t("avgBtn")}
          </Button>
        </div>
      );
    } else if (type === "Personal grooming (haircuts, toiletries, etc.)") {
      return (
        <div className="avg-value-div d-flex  align-items-center justify-content-between w-100">
          <div className="avg-value-text">
            {t("expenses.additionalExpenses.3")}
          </div>
          <Button
            className="avg-value-btn"
            onClick={() => handleAvgValue("60", type)}
          >
            {t("avgBtn")}
          </Button>
        </div>
      );
    } else if (type === "Dental care") {
      return (
        <div className="avg-value-div d-flex  align-items-center justify-content-between w-100">
          <div className="avg-value-text">
            {t("expenses.additionalExpenses.6")}
          </div>
          <Button
            className="avg-value-btn"
            onClick={() => handleAvgValue("379", type)}
          >
            {t("avgBtn")}
          </Button>
        </div>
      );
    } else if (type === "Laundromat") {
      return (
        <div className="avg-value-div d-flex  align-items-center justify-content-between w-100">
          <div className="avg-value-text">
            {userData?.livingInCanada === "yes"
              ? t("expenses.additionalExpenses.4")
              : "On average, people typically spend between $3 and $5 on a load of clothes. We recommend budgeting between $24 and $40 a month if you don’t have free access to laundry."}
          </div>
        </div>
      );
    } else if (type === "Alcohol/smoking, etc.") {
      return (
        <div className="avg-value-div d-flex  align-items-center justify-content-between w-100">
          <div className="avg-value-text">
            {t("expenses.additionalExpenses.13")}
          </div>
          <Button
            className="avg-value-btn"
            onClick={() => handleAvgValue("68", type)}
          >
            {t("avgBtn")}
          </Button>
        </div>
      );
    } else if (type === "Gym memberships") {
      return (
        <div className="avg-value-div d-flex  align-items-center justify-content-between w-100">
          <div className="avg-value-text">
            {t("expenses.additionalExpenses.16")}
          </div>
        </div>
      );
    } else if (type === "Post-secondary education tuition") {
      return (
        <div className="avg-value-div d-flex  align-items-center justify-content-between w-100">
          <div className="avg-value-text">
            {t("expenses.additionalExpenses.17")}
          </div>
        </div>
      );
    } else if (type === "Post-secondary education expenses (textbooks, etc.)") {
      return (
        <div className="avg-value-div d-flex  align-items-center justify-content-between w-100">
          <div className="avg-value-text">
            {t("expenses.additionalExpenses.18")}
          </div>
          <Button
            className="avg-value-btn"
            onClick={() => handleAvgValue("75", type)}
          >
            {t("avgBtn")}
          </Button>
        </div>
      );
    } else if (
      type ===
      "Specialists (massage therapist, physical therapist, counselling, etc.)"
    ) {
      return (
        <div className="avg-value-div d-flex  align-items-center justify-content-between w-100">
          <div className="avg-value-text">
            {t("expenses.additionalExpenses.7")}
          </div>
        </div>
      );
    } else if (type === "Eye care") {
      return (
        <div className="avg-value-div d-flex  align-items-center justify-content-between w-100">
          <div className="avg-value-text">
            {t("expenses.additionalExpenses.8")}
          </div>
        </div>
      );
    }
  };
  const getRangeLinkContent = (content, link, className) => {
    return (
      <div className={`row if-no-select-show-screen mb-bottom-24 ${className}`}>
        <div className="col-12 col-sm-12 col-md-8 col-lg-8 col-xl-9">
          <h5 className="mb-0 w-auto">{content}</h5>
        </div>
        <div className="col-12 col-sm-12 col-md-4 col-lg-4 col-xl-3">
          <Button
            className="no-employee-btn"
            onClick={() => {
              window.open(link);
            }}
          >
            {t("newTab")}
          </Button>
        </div>
      </div>
    );
  };

  const getGeneralInfo = (type) => {
    if (type === "Laundromat") {
      return t("expenses.additionalExpenses.19");
    } else if (type === "Donations") {
      return t("expenses.additionalExpenses.20");
    }
  };

  const getTypeText = (type) => {
    if (type === "Meal prep and delivery services") {
      return t("expenses.additionalExpenses.14");
    } else if (type === "Gym memberships") {
      return t("expenses.additionalExpenses.15");
    } else if (type === "Immigration Expenses") {
      return t("expenses.additionalExpenses.9");
    }
  };
  return (
    <div
      className={`row mx-0 profile-second-card-row ${IS_REVIEW ? "mb-3" : ""}`}
    >
      <div className="col-12">
        <div className="row">
          <div className="col-12 profile-card-inner">
            <h5 className="d-flex justify-content-between align-items-center">
              {userData?.livingInCanada === "yes"
                ? t("expenses.ques36.title")
                : "How much do you think these additional expenses will cost you each month?"}
              {IS_REVIEW && !editableStep && (
                <Button
                  className="auth-theme-btn auth-theme-btn-outline theme-inner-btn d-flex align-items-center"
                  onClick={() => handleEditableStep(14)}
                >
                  {t("editBtn")}
                </Button>
              )}
            </h5>
          </div>
        </div>
        <div className="row">
          <div className="col-12">
            {formik.values.incomeArr?.length > 0 &&
              (editableStep == 14 || (IS_REVIEW && editableStep)
                ? formik.values.incomeArr.map((item, index) => {
                    const genInfo = getGeneralInfo(item.type.en);
                    return (
                      <>
                        <CreateProfileCardWrapper collapseInnerCard={true}>
                          <>
                            <h5
                              className={`d-flex align-items-center justify-content-between ${
                                genInfo ? "" : "100%"
                              }`}
                            >
                              <div className="d-flex align-items-center">
                                {item.type?.[i18n.language]}
                                {genInfo && (
                                  <Button
                                    style={{ marginLeft: "10px" }}
                                    className="info-circle-btn bg-transparent border-0 p-0 w-auto shadow-none h-auto"
                                    onClick={() => {
                                      dispatch(setInfoContent(genInfo));
                                    }}
                                  >
                                    <img
                                      src="/assets/images/icons/info-circle-icon.svg"
                                      alt=""
                                      className="img-fluid"
                                    />
                                  </Button>
                                )}
                              </div>
                              <div className="d-flex align-items-center">
                                <Button className="p-0 w-auto bg-transparent border-0 h-auto">
                                  <img
                                    src="/assets/images/icons/minus-red-icon.svg"
                                    alt=""
                                    className="img-fluid"
                                    onClick={() => {
                                      handleRemoveCategory(item);
                                    }}
                                  />
                                </Button>
                              </div>
                            </h5>
                            <p>{getTypeText(item.type.en)}</p>
                            {item.type?.en?.includes("Other") ? (
                              <>
                                <Input
                                  rootClassName="type-input-custom"
                                  placeholder={t(
                                    "expenses.ques35.placeholder1"
                                  )}
                                  name={`incomeArr[${index}].title`}
                                  value={formik.values.incomeArr[index].title}
                                  onChange={formik.handleChange}
                                />
                                <Input
                                  style={{
                                    marginTop: "15px",
                                  }}
                                  suffix={
                                    formik.values.incomeArr[index]?.amount ? (
                                      <img
                                        src="/assets/images/icons/primary-check-icon.svg"
                                        alt=""
                                        className=""
                                      />
                                    ) : (
                                      ""
                                    )
                                  }
                                  prefix={
                                    formik.values.incomeArr[index].amount
                                      ? "$"
                                      : " "
                                  }
                                  placeholder={t("expenses.commonPlaceholder")}
                                  name={`incomeArr[${index}].amount`}
                                  onChange={formik.handleChange}
                                  onKeyDown={handleKeyDown}
                                  value={formatNumber(
                                    formik.values.incomeArr[index].amount
                                  )}
                                />
                              </>
                            ) : (
                              <Input
                                prefix={
                                  formik.values.incomeArr[index]?.amount
                                    ? "$"
                                    : " "
                                }
                                placeholder={t("expenses.commonPlaceholder")}
                                name={`incomeArr[${index}].amount`}
                                onChange={formik.handleChange}
                                onKeyDown={handleKeyDown}
                                value={formatNumber(
                                  formik.values.incomeArr[index]?.amount
                                )}
                                suffix={
                                  formik.values.incomeArr[index]?.amount ? (
                                    <img
                                      src="/assets/images/icons/primary-check-icon.svg"
                                      alt=""
                                      className=""
                                    />
                                  ) : (
                                    ""
                                  )
                                }
                              />
                            )}
                            {getAvgRangeComponent(item.type?.en)}
                          </>
                        </CreateProfileCardWrapper>
                        {getInfoComponent(item.type?.en)}
                      </>
                    );
                  })
                : formik.values.incomeArr.map((item, index) => {
                    return (
                      <CreateProfileCardWrapper collapseInnerCard={true}>
                        <div className="row create-profile-details-row">
                          <div className="col-12">
                            <h6 className="mb-1 d-flex align-items-center justify-content-between">
                              {item.type?.[i18n.language]}
                              <Button className="p-0 w-auto bg-transparent border-0 h-auto">
                                <img
                                  src="/assets/images/icons/minus-red-icon.svg"
                                  alt=""
                                  className="img-fluid"
                                  onClick={() => {
                                    handleRemoveCategory(item);
                                  }}
                                />
                              </Button>
                            </h6>
                            {item.type?.en?.includes("Other") && (
                              <p>{item?.title}</p>
                            )}
                            <ul className="list-inline mb-0">
                              <li className="list-inline-item">
                                <span className="time-tag">${item.amount}</span>
                              </li>
                              <li className="list-inline-item">
                                <span
                                  style={{
                                    background: "#BFBFBF",
                                  }}
                                  className="dot-tag"
                                />
                              </li>
                              <li className="list-inline-item">
                                <span className="time-tag">
                                  {t("monthlyTag")}
                                </span>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </CreateProfileCardWrapper>
                    );
                  }))}
          </div>
        </div>
        {!IS_REVIEW && (
          <Button
            className="avg-value-btn forgot-to-btn"
            onClick={() => {
              setInnerStepper(innerStepper - 1);
              handleEditableStep(null);
            }}
          >
            {t("expenses.ques35.forgotBtn")}
          </Button>
        )}
      </div>
    </div>
  );
};

const Entertainment = ({ formik }) => {
  const userData = useSelector((state) => state.form.userData);
  const { t } = useTranslation();
  return (
    <>
      <CreateProfileCardWrapper>
        <h5 className="d-flex align-items-center justify-content-between">
          {userData?.livingInCanada === "yes"
            ? t("expenses.ques31.title")
            : "How much do you think you’ll spend on entertainment each month?"}
        </h5>
        <p>{t("expenses.ques31.helperText")}</p>
        <Input
          prefix={formik.values.entertainmentCost ? "$" : " "}
          placeholder={t("expenses.commonPlaceholder")}
          name="entertainmentCost"
          onChange={formik.handleChange}
          onKeyDown={handleKeyDown}
          value={formatNumber(formik.values.entertainmentCost)}
          suffix={
            formik.values.entertainmentCost ? (
              <img
                src="/assets/images/icons/primary-check-icon.svg"
                alt=""
                className=""
              />
            ) : (
              ""
            )
          }
          className={getErrorDataClassName("entertainmentCost", formik)}
        />
        {formik.errors["entertainmentCost"] &&
          formik.touched["entertainmentCost"] && (
            <Typography textAlign={"left"} variant="body2" color="red">
              {formik.errors["entertainmentCost"]}
            </Typography>
          )}
      </CreateProfileCardWrapper>
    </>
  );
};

const SavingUp = ({ formik, handleEditableStep, IS_REVIEW }) => {
  const { t } = useTranslation();
  const [savingCategories, setSavingCategories] = useState([]);
  const dispatch = useDispatch();
  useEffect(() => {
    getSavingsCategoriesList();
  }, []);
  const getSavingsCategoriesList = () => {
    dispatch(setLoading(true));
    getCategories("savings")
      .then((resp) => {
        setSavingCategories(resp.data.category);
      })
      .catch((Err) => {
        message.error(Err.message[i18n.language]);
      })
      .finally(() => {
        dispatch(setLoading(false));
      });
  };

  return (
    <>
      <CreateProfileCardWrapper>
        <p>{t("expenses.ques32.helperText1")}</p>
        <h5>{t("expenses.ques32.title")}</h5>
        <p> {t("expenses.ques32.helperText2")}</p>
        <Checkbox.Group
          className="custom-checkbox-select-button"
          name={"savingsArr"}
          style={{ width: "100%" }}
          onChange={(e) => {
            const savingsAmtArr =
              formik.values?.savingsAmtArr?.length > 0
                ? [...formik.values.savingsAmtArr]
                : [];
            let initArr = [];
            formik.setFieldValue("savingsArr", e);
            initArr = savingCategories
              .filter((i) => e.some((n) => n === i.title.en))
              .map((i) => i.title)
              .map((i) => {
                return {
                  type: i,
                  title: savingsAmtArr?.find(
                    (n) => i.en === n.type?.en && !n.isManuallyAdded
                  )?.title || "",
                  amount:
                    savingsAmtArr?.find(
                      (n) => i.en === n.type?.en && !n.isManuallyAdded
                    )?.amount || "",
                };
              });
            savingsAmtArr.forEach((item) => {
              if (item?.isManuallyAdded) {
                initArr.push(item);
              }
            });
            formik.setFieldValue("savingsAmtArr", initArr);
            if (IS_REVIEW) {
              handleEditableStep(17);
            }
          }}
          value={formik.values?.savingsArr}
        >
          <ul className="list-inline mb-0 additional-income-list">
            {savingCategories.map((item) => {
              return (
                <li className="list-inline-item" key={item?._id}>
                  <Checkbox
                    className="additional-income-btn"
                    value={item.title?.en}
                  >
                    {item.title[i18n.language]}
                    <img
                      src="/assets/images/icons/close-icon-white-small.svg"
                      alt=""
                      className="img-fluid close-icon-img"
                    />
                  </Checkbox>
                </li>
              );
            })}
          </ul>
        </Checkbox.Group>
      </CreateProfileCardWrapper>
      <div className="row if-no-select-show-screen three-line mb-bottom-24">
        <div className="col-12 col-sm-12 col-md-8 col-lg-8 col-xl-9">
          <h5 className="mb-0 w-auto">{t("expenses.resources.24")}</h5>
        </div>
        <div className="col-12 col-sm-12 col-md-4 col-lg-4 col-xl-3">
          <Button
            onClick={() => {
              window.open(
                "https://www.creditcanada.com/blog/how-to-create-financial-goals"
              );
            }}
            className="no-employee-btn"
          >
            {t("newTab")}
          </Button>
        </div>
      </div>
    </>
  );
};
const SavingsVacationsRetirementExpenses = ({
  formik,
  editableStep,
  handleEditableStep,
  innerStepper,
  IS_REVIEW,
  handleKeyPress,
  setInnerStepper,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const handleRemoveCategory = (item) => {
    const filteredSavingsArr = formik.values.savingsArr.filter(
      (i) => i != item.type && i != item.type?.en
    );
    const filteredSavingsAmtArr = formik.values.savingsAmtArr.filter(
      (i) => i.type != item.type
    );
    formik.setFieldValue("savingsArr", filteredSavingsArr);
    formik.setFieldValue("savingsAmtArr", filteredSavingsAmtArr);
  };

  useEffect(() => {
    if (formik.values?.savingsArr?.length > 0) {
      getSavingsCategoriesList();
    }
  }, [formik.values?.savingsAmtArr?.length, formik.values?.savingsArr?.length]);
  const getSavingsCategoriesList = () => {
    dispatch(setLoading(true));
    getCategories("savings")
      .then((resp) => {
        if (
          resp.data.category?.length > 0 &&
          formik.values?.savingsAmtArr?.length > 0
        ) {
          const savingsAmtArr = [...formik.values?.savingsAmtArr];
          let initArr = [];
          
          const filteredCategories = resp.data.category
            .filter((i) =>
              formik.values?.savingsAmtArr.some(
                (n) =>
                  (n.type?.en === i.title?.en || n?.type === i.title.en) &&
                  !n?.isManuallyAdded
              )
            )
            .map((i) => i.title);
          if (filteredCategories?.length > 0) {
            filteredCategories.forEach((i) => {
              initArr.push({
                type: i,
                title:
                  savingsAmtArr?.find(
                    (n) =>
                      (i.en === n.type?.en || i.en === n.type) &&
                      !n?.isManuallyAdded
                  )?.title || "",
                amount:
                  savingsAmtArr?.find(
                    (n) =>
                      (i.en === n.type?.en || i.en === n.type) &&
                      !n?.isManuallyAdded
                  )?.amount || "",
              });
            });
          }
          savingsAmtArr.forEach((item) => {
            if (item?.isManuallyAdded) {
              initArr.push(item);
            }
          });
          formik.setFieldValue("savingsAmtArr", initArr);
        }
      })
      .catch((Err) => {
        message.error(Err.message[i18n.language]);
      })
      .finally(() => {
        dispatch(setLoading(false));
      });
  };
  return (
    <div
      className={`row mx-0 profile-second-card-row ${IS_REVIEW ? "mb-3" : ""}`}
    >
      <div className="col-12">
        <div className="row">
          <div className="col-12 profile-card-inner">
            <h5 className="d-flex justify-content-between align-items-center">
              {t("expenses.ques35.title")}{" "}
              {IS_REVIEW &&
                !editableStep &&
                formik.values?.savingsAmtArr?.length > 0 && (
                  <Button
                    className="auth-theme-btn auth-theme-btn-outline theme-inner-btn d-flex align-items-center"
                    onClick={() => handleEditableStep(17)}
                  >
                    {t("editBtn")}
                  </Button>
                )}
            </h5>
          </div>
        </div>
        <div className="row">
          <div className="col-12">
            {formik.values.savingsAmtArr?.length > 0 &&
              (editableStep === 17 || (IS_REVIEW && editableStep)
                ? formik.values.savingsAmtArr.map((item, index) => {
                    return (
                      <>
                        <CreateProfileCardWrapper collapseInnerCard={true}>
                          <h5 className="d-flex align-items-center w-100 justify-content-between">
                            <div className="d-flex align-items-center">
                              {item?.isManuallyAdded
                                ? t("expenses.ques35.cardTitle")
                                : item.type[i18n.language]}
                              {item.type.en?.includes("Emergency") && (
                                <Button
                                  style={{ marginLeft: "10px" }}
                                  className="border-0 bg-tranaparent w-auto h-auto shadow-none p-0"
                                  onClick={() => {
                                    dispatch(
                                      setInfoContent(t("expenses.resources.29"))
                                    );
                                  }}
                                >
                                  <img
                                    src="/assets/images/icons/info-circle-icon.svg"
                                    alt=""
                                    className="img-fluid"
                                  />
                                </Button>
                              )}
                            </div>
                            <div className="d-flex align-items-center">
                              <Button className="p-0 w-auto bg-transparent border-0 h-auto">
                                <img
                                  src="/assets/images/icons/minus-red-icon.svg"
                                  alt=""
                                  className="img-fluid"
                                  onClick={() => {
                                    handleRemoveCategory(item);
                                  }}
                                />
                              </Button>
                            </div>
                          </h5>
                          {item?.isManuallyAdded ? (
                            <>
                              <Input
                                rootClassName="type-input-custom"
                                placeholder={t("expenses.ques35.placeholder1")}
                                name={`savingsAmtArr[${index}].type`}
                                value={formik.values.savingsAmtArr[index]?.type}
                                onChange={formik.handleChange}
                              />
                              <Input
                                style={{
                                  marginTop: "15px",
                                }}
                                prefix={
                                  formik.values.savingsAmtArr[index]?.amount
                                    ? "$"
                                    : " "
                                }
                                placeholder={t("expenses.commonPlaceholder")}
                                name={`savingsAmtArr[${index}].amount`}
                                onChange={formik.handleChange}
                                onKeyDown={handleKeyDown}
                                value={formatNumber(
                                  formik.values.savingsAmtArr[index]?.amount
                                )}
                                suffix={
                                  formik.values.savingsAmtArr[index]?.amount ? (
                                    <img
                                      src="/assets/images/icons/primary-check-icon.svg"
                                      alt=""
                                      className=""
                                    />
                                  ) : (
                                    ""
                                  )
                                }
                              />
                            </>
                          ) : (
                            <>
                              {item?.type?.en?.includes("Other") && (
                                <Input
                                  rootClassName="type-input-custom"
                                  placeholder={t(
                                    "expenses.ques35.placeholder1"
                                  )}
                                  name={`savingsAmtArr[${index}].title`}
                                  value={
                                    formik.values.savingsAmtArr[index]?.title
                                  }
                                  onChange={formik.handleChange}
                                />
                              )}
                              <Input
                                style={{
                                  marginTop: "15px",
                                }}
                                prefix={
                                  formik.values.savingsAmtArr[index]?.amount
                                    ? "$"
                                    : " "
                                }
                                placeholder={t("expenses.commonPlaceholder")}
                                name={`savingsAmtArr[${index}].amount`}
                                onChange={formik.handleChange}
                                onKeyDown={handleKeyDown}
                                value={formatNumber(
                                  formik.values.savingsAmtArr[index]?.amount
                                )}
                                suffix={
                                  formik.values.savingsAmtArr[index]?.amount ? (
                                    <img
                                      src="/assets/images/icons/primary-check-icon.svg"
                                      alt=""
                                      className=""
                                    />
                                  ) : (
                                    ""
                                  )
                                }
                              />
                            </>
                          )}
                        </CreateProfileCardWrapper>
                      </>
                    );
                  })
                : formik.values.savingsAmtArr.map((item, index) => {
                    return (
                      <CreateProfileCardWrapper collapseInnerCard={true}>
                        <div className="row create-profile-details-row">
                          <div className="col-12">
                            <h6 className="mb-1 d-flex align-items-center">
                              {item?.isManuallyAdded
                                ? t("expenses.ques35.cardTitle")
                                : item.type[i18n.language]}
                              <Button className="p-0 w-auto bg-transparent border-0 h-auto">
                                <img
                                  src="/assets/images/icons/minus-red-icon.svg"
                                  alt=""
                                  className="img-fluid"
                                  onClick={() => {
                                    handleRemoveCategory(item);
                                  }}
                                />
                              </Button>
                            </h6>
                            {item?.isManuallyAdded && <p>{item?.type}</p>}
                            {!item?.isManuallyAdded &&
                              item?.type?.en?.includes("Other") && (
                                <p>{item?.title}</p>
                              )}
                            <ul className="list-inline mb-0">
                              <li className="list-inline-item">
                                <span className="time-tag">
                                  $ {item.amount}
                                </span>
                              </li>
                              <li className="list-inline-item">
                                <span
                                  style={{
                                    background: "#BFBFBF",
                                  }}
                                  className="dot-tag"
                                />
                              </li>
                              <li className="list-inline-item">
                                <span className="time-tag">
                                  {t("monthlyTag")}
                                </span>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </CreateProfileCardWrapper>
                    );
                  }))}
            <CreateProfileCardWrapper
              onClick={() => {
                
                const obj = {
                  type: "",
                  title: "Others",
                  amount: "",
                  isManuallyAdded: true,
                };
                const savingsAmtArr =
                  formik.values?.savingsAmtArr?.length > 0
                    ? [...formik.values?.savingsAmtArr]
                    : [];
                savingsAmtArr.push(obj);
                formik.setFieldValue("savingsAmtArr", savingsAmtArr);
                handleEditableStep(17);
              }}
              role="button"
            >
              <h5 className="d-flex align-items-center mb-0 w-100 justify-content-between">
                {t("expenses.ques35.btnTxt")}
                <Button className="p-0 w-auto bg-transparent border-0 h-auto">
                  <img
                    src="/assets/images/icons/plus-icon-green.svg"
                    alt=""
                    className="img-fluid"
                  />
                </Button>
              </h5>
            </CreateProfileCardWrapper>
          </div>
        </div>
        {!IS_REVIEW && (
          <Button
            className="avg-value-btn forgot-to-btn"
            onClick={() => {
              setInnerStepper(innerStepper - 1);
              handleEditableStep(null);
            }}
          >
            {t("expenses.ques35.forgotBtn")}
          </Button>
        )}
      </div>
    </div>
  );
};
const LoansBackHomeExpenses = ({ formik, handleEditableStep, IS_REVIEW }) => {
  const { t } = useTranslation();
  return (
    <>
      <CreateProfileCardWrapper>
        <h5 className="d-flex align-items-center justify-content-between">
          {t("expenses.ques33.title")}
        </h5>
        <p>{t("expenses.ques33.helperText")}</p>
        <Radio.Group
          className="w-100"
          defaultValue={1}
          name="debtFlag"
          value={formik.values?.debtFlag}
          onChange={(e) => {
            if (e.target.value === "no" && IS_REVIEW) {
              handleEditableStep(null);
            } else if (e.target.value === "yes" && IS_REVIEW) {
              handleEditableStep(19);
            }
            formik.setFieldValue("debtFlag", e.target.value);
            formik.handleChange(e);
          }}
        >
          <ul className="list-inline mb-0">
            <li>
              <Radio value={"yes"}>{t("expenses.ques33.options.1")}</Radio>
            </li>
            <li>
              <Radio value={"no"}>{t("expenses.ques33.options.2")}</Radio>
            </li>
          </ul>
        </Radio.Group>
        {formik.errors["debtFlag"] && (
          <Typography textAlign={"left"} variant="body2" color="red">
            {formik.errors["debtFlag"]}
          </Typography>
        )}
      </CreateProfileCardWrapper>
      <div className="row if-no-select-show-screen mb-bottom-24">
        <div className="col-12 col-sm-12 col-md-8 col-lg-8 col-xl-9">
          <h5 className="mb-0 w-auto">{t("expenses.resources.25")}</h5>
        </div>
        <div className="col-12 col-sm-12 col-md-4 col-lg-4 col-xl-3">
          <Button
            className="no-employee-btn"
            onClick={() => {
              window.open("https://www.creditcanada.com/contact-us");
            }}
          >
            {t("newTab")}
          </Button>
        </div>
      </div>
      <div className="row if-no-select-show-screen mb-bottom-24 last-element">
        <div className="col-12 col-sm-12 col-md-8 col-lg-8 col-xl-9">
          <h5 className="mb-0 w-auto">{t("expenses.resources.26")}</h5>
        </div>
        <div className="col-12 col-sm-12 col-md-4 col-lg-4 col-xl-3">
          <Button
            className="no-employee-btn"
            onClick={() => {
              window.open("https://www.creditcanada.com/elearning-newcomers");
            }}
          >
            {t("newTab")}
          </Button>
        </div>
      </div>
    </>
  );
};
const AboutDebt = ({
  formik,
  editableStep,
  handleEditableStep,
  debtObj,
  handleDebtObj,
  IS_REVIEW,
  handleKeyPress,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const handleChange = (value, name, idx) => {
    const debtArr = [...formik.values.debtArr];
    debtArr[idx][name] = value || "";
    formik.setFieldValue("debtArr", debtArr);
  };
  const handleRemoveDebt = (item) => {
    const debtArr = [...formik.values?.debtArr];
    const filteredArr = debtArr.filter((i) => i.id != item.id);
    formik.setFieldValue("debtArr", filteredArr);
  };
  const handleEdit = (item) => {
    handleEditableStep(19);
  };

  useEffect(() => {
    if (formik.values?.debtArr?.length === 0) {
      const debtObj = {
        title: "",
        minPay: "",
        interestRate: "",
        totalDebt: "",
      };
      const debtArr = [debtObj];
      formik.setFieldValue("debtArr", debtArr);
      handleEditableStep(19);
    }
  }, [formik.values?.debtArr?.length]);
  return (
    <div className="row mx-0 profile-second-card-row">
      <div className="col-12">
        <div className="row">
          <div className="col-12 profile-card-inner">
            <h5 className="d-flex align-items-center justify-content-between">
              {t("expenses.ques34.title")}
              <Button
                className="border-0 bg-tranaparent w-auto h-auto shadow-none p-0 info-icon-btn"
                onClick={() => {
                  dispatch(setInfoContent(t("expenses.resources.27")));
                }}
              >
                <img
                  src="/assets/images/icons/info-circle-icon.svg"
                  alt=""
                  className="img-fluid"
                />
              </Button>
            </h5>
            {IS_REVIEW && !editableStep && (
              <div className="col-12 d-flex justify-content-end">
                <Button
                  className="auth-theme-btn auth-theme-btn-outline theme-inner-btn d-flex align-items-center"
                  onClick={() => handleEditableStep(19)}
                >
                  {t("editBtn")}
                </Button>
              </div>
            )}
          </div>
        </div>
        <div className="row">
          <div className="col-12">
            {formik.values?.debtArr?.map((item, idx) => {
              if (editableStep === 19 || (IS_REVIEW && editableStep)) {
                return (
                  <>
                    <CreateProfileCardWrapper collapseInnerCard={true}>
                      <h5
                        className={`d-flex w-100 align-items-center justify-content-${
                          idx === 0 ? "start" : "between"
                        }`}
                      >
                        {t("expenses.ques34.child.title1")}
                        {idx !== 0 && (
                          <Button className="p-0 w-auto bg-transparent border-0 h-auto">
                            <img
                              src="/assets/images/icons/minus-red-icon.svg"
                              alt=""
                              className="img-fluid"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveDebt(item);
                              }}
                            />
                          </Button>
                        )}
                      </h5>
                      <Input
                        suffix={
                          <img
                            src="/assets/images/icons/primary-check-icon.svg"
                            alt=""
                            className=""
                            style={
                              item?.title
                                ? { display: "block" }
                                : { display: "none" }
                            }
                          />
                        }
                        placeholder={t("expenses.ques34.child.placeholder1")}
                        value={item?.title}
                        onChange={(e) =>
                          handleChange(e.target.value, "title", idx)
                        }
                      />
                    </CreateProfileCardWrapper>
                    <CreateProfileCardWrapper collapseInnerCard={true}>
                      <h5 className="d-flex align-items-center">
                        {t("expenses.ques34.child.title2")}
                      </h5>
                      <Input
                        suffix={
                          <img
                            src="/assets/images/icons/primary-check-icon.svg"
                            alt=""
                            className=""
                            style={
                              item?.totalDebt
                                ? { display: "block" }
                                : { display: "none" }
                            }
                          />
                        }
                        prefix={item?.totalDebt ? "$" : " "}
                        placeholder={t("expenses.totalAmt")}
                        onKeyDown={handleKeyDown}
                        value={formatNumber(item?.totalDebt)}
                        onChange={(e) =>
                          handleChange(e.target.value, "totalDebt", idx)
                        }
                      />
                    </CreateProfileCardWrapper>
                    <CreateProfileCardWrapper collapseInnerCard={true}>
                      <h5 className="d-flex align-items-cent">
                        {t("expenses.ques34.child.title3")}
                      </h5>
                      <Input
                        suffix={
                          <img
                            src="/assets/images/icons/primary-check-icon.svg"
                            alt=""
                            className=""
                            style={
                              item?.minPay
                                ? { display: "block" }
                                : { display: "none" }
                            }
                          />
                        }
                        prefix={item?.minPay ? "$" : " "}
                        placeholder={t("expenses.commonPlaceholder")}
                        onKeyDown={handleKeyDown}
                        value={formatNumber(item?.minPay)}
                        onChange={(e) =>
                          handleChange(e.target.value, "minPay", idx)
                        }
                      />
                    </CreateProfileCardWrapper>
                    <CreateProfileCardWrapper collapseInnerCard={true}>
                      <h5 className="d-flex align-items-center">
                        {t("expenses.ques34.child.title4")}
                      </h5>
                      <Input
                        suffix={
                          <img
                            src="/assets/images/icons/primary-check-icon.svg"
                            alt=""
                            className=""
                            style={
                              item?.interestRate
                                ? { display: "block" }
                                : { display: "none" }
                            }
                          />
                        }
                        placeholder={t("expenses.ques34.child.placeholder4")}
                        type="number"
                        onKeyDown={handleKeyPress}
                        min={0}
                        value={item?.interestRate}
                        onChange={(e) =>
                          handleChange(e.target.value, "interestRate", idx)
                        }
                      />
                    </CreateProfileCardWrapper>
                  </>
                );
              }
              return (
                <CreateProfileCardWrapper>
                  <h5
                    className="d-flex align-items-center justify-content-between w-100"
                    onClick={() => handleEdit(item)}
                  >
                    {item.title}
                    <Button className="p-0 w-auto bg-transparent border-0 h-auto">
                      <img
                        src="/assets/images/icons/minus-red-icon.svg"
                        alt=""
                        className="img-fluid"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveDebt(item);
                        }}
                      />
                    </Button>
                  </h5>
                </CreateProfileCardWrapper>
              );
            })}
          </div>
          <div className="col-12">
            <CreateProfileCardWrapper
              onClick={() => {
                let debtArr;
                let debtObj = {
                  title: "",
                  minPay: "",
                  interestRate: "",
                  totalDebt: "",
                };
                if (formik.values.debtArr?.length > 0) {
                  debtArr = [...formik.values.debtArr];
                  debtArr.push({ ...debtObj, id: debtArr?.length + 1 });
                } else {
                  debtArr = [{ ...debtObj, id: 1 }];
                }
                formik.setFieldValue("debtArr", debtArr);
                handleEditableStep(19);
              }}
              role="button"
            >
              <h5 className="d-flex align-items-center mb-0 w-100 justify-content-between">
                {t("expenses.ques35.btnTxt")}
                <Button className="p-0 w-auto bg-transparent border-0 h-auto">
                  <img
                    src="/assets/images/icons/plus-icon-green.svg"
                    alt=""
                    className="img-fluid"
                  />
                </Button>
              </h5>
            </CreateProfileCardWrapper>
          </div>
        </div>
      </div>
    </div>
  );
};
const ReviewInformation = () => {
  return (
    <>
      <div className="row">
        <div className="col-12 review-inform-div">
          <h6 className="review-tag">Review Your Information</h6>
          <CreateProfileCardWrapper>
            <h5 className="d-flex align-items-center justify-content-between">
              Are you currently employed?
              <Button className="border-0 bg-tranaparent w-auto h-auto shadow-none p-0 info-icon-btn">
                <img
                  src="/assets/images/icons/info-circle-icon.svg"
                  alt=""
                  className="img-fluid"
                />
              </Button>
            </h5>
            <Radio.Group className="w-100" name="radiogroup" defaultValue={1}>
              <ul className="list-inline mb-0">
                <li>
                  <Radio value={1}>Yes</Radio>
                </li>
              </ul>
            </Radio.Group>
          </CreateProfileCardWrapper>
          <CreateProfileCardWrapper>
            <h5 className="d-flex align-items-center justify-content-between">
              What kind of work do you do?
              <Button className="border-0 bg-tranaparent w-auto h-auto shadow-none p-0 info-icon-btn">
                <img
                  src="/assets/images/icons/info-circle-icon.svg"
                  alt=""
                  className="img-fluid"
                />
              </Button>
            </h5>
            <Select
              suffixIcon={
                <img
                  src="/assets/images/icons/down-arrow-gray-icon.svg"
                  alt=""
                  className="img-fluid"
                  role="button"
                />
              }
              className="custom-common-select-box"
              name="profession"
              placeholder="Select Profession"
              options={[
                {
                  value: "architect",
                  label: "Architect",
                },
                {
                  value: "computer_eng",
                  label: "Computer Engineer",
                },
                {
                  value: "civil_eng",
                  label: "Civil Engineer",
                },
                {
                  value: "sales_executive",
                  label: "Sales executive",
                },
                {
                  value: "sales_manager",
                  label: "Sales Manager",
                },
              ]}
            />
          </CreateProfileCardWrapper>
          <CreateProfileCardWrapper>
            <h5>Is this job full time or part time?</h5>
            <Radio.Group className="custom-radio-select-button" name="jobType">
              <Radio.Button value="a">Full Time</Radio.Button>
              <Radio.Button value="b">Part Time</Radio.Button>
            </Radio.Group>
          </CreateProfileCardWrapper>
        </div>
      </div>
    </>
  );
};
export const FinalSteps = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isMobile = window.innerWidth < 768;
  const containerRef = useRef();
  const { t } = useTranslation();
  useEffect(() => {
    if (containerRef) {
      scrollToTop();
    }
  }, [containerRef]);


  const scrollToTop = () => {
    if (containerRef.current) {
      containerRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };
  return (
    <div ref={containerRef} className="onboarding-container">
      <div className="row mx-0 phase-2-complete-row position-relative">
        <div className="successful-video-tag">
          <img
            alt=""
            src="/assets/images/icons/phase-completion-icon.svg"
            className="completion-icon"
          />
          <img
            src={
              isMobile
                ? "/assets/gifs/mobile-celeb.gif"
                : "/assets/gifs/web-celeb.gif"
            }
            alt="img-fluid"
            className="completion-gif"
          />
        </div>
        <div className="col-12 successful-tag-inner">
          <h1 className="mb-2">{t("expenses.phase3.title")} </h1>
          <h6 className="mb-2">{t("expenses.phase3.headline1")}</h6>
          <h6 className="mb-2">{t("expenses.phase3.p1")}</h6>
          <h6 className="mb-2">{t("expenses.phase3.p2")}</h6>
          <h6>
            {t("expenses.phase3.p3")}
            <br />
            {t("expenses.phase3.p4")}
          </h6>
        </div>
      </div>
      <CreateProfileCardWrapper>
        <h2>{t("expenses.phase3.subTitle1")}</h2>
        <h3>{t("expenses.phase3.subTitle2")}</h3>
        <h4 className="mb-2">{t("expenses.phase3.p5")}</h4>
        <h4 className="mb-2">{t("expenses.phase3.p6")}</h4>
        <h4 className="mb-3">{t("expenses.phase3.p7")}</h4>
        <h3 className="mb-2">{t("expenses.phase3.p8")}</h3>
        <h4 className="mb-2">{t("expenses.phase3.p9")}</h4>
        <h4 className="mb-2">
          <ul>
            <li>{t("expenses.phase3.p13")}</li>
            <li>{t("expenses.phase3.p10")}</li>
            <li>{t("expenses.phase3.p11")}</li>
          </ul>
        </h4>
        <h4>{t("expenses.phase3.p12")}</h4>
        <Button
          className="auth-theme-btn process-with-phase-btn"
          onClick={() => {
            dispatch(
              setStepperStatus({
                isProfileCompleted: true,
                isIncomeCompleted: true,
                isExpenseCompleted: true,
              })
            );
            dispatch(setTotalQuestions(31));
            dispatch(setAnsweredQuestions(0));
            navigate(routePaths.dashboard);
            dispatch(setInnerStepper(0));
          }}
        >
          {t("expenses.viewBudget")}
        </Button>
      </CreateProfileCardWrapper>
    </div>
  );
};
const stepper = [
  {
    step: 0,
    Component: LivingType,
    questions: 1,
  },
  {
    step: 1,
    Component: Mortgage,
    questions: 1,
  },
  {
    step: 2,
    Component: HomeMaintenance,
    questions: 3,
  },
  {
    step: 3,
    Component: RentInsurance,
    questions: 2,
  },
  {
    step: 4,
    Component: Utilities,
    questions: 1,
  },
  {
    step: 5,
    Component: BasicExpenses,
    questions: 5,
  },
  {
    step: 6,
    Component: MoneySend,
    questions: 2,
  },
  {
    step: 7,
    Component: PersonalVehicles,
    questions: 2,
  },
  {
    step: 8,
    Component: MoneyMaking,
    questions: 1,
  },
  {
    step: 9,
    Component: PayInsuranceMoney,
    questions: 1,
  },
  {
    step: 10,
    Component: AdditionalExpensesMaintenanceGas,
    questions: 2,
  },
  {
    step: 11,
    Component: SpendTransportationExpenses,
    questions: 1,
  },
  {
    step: 12,
    Component: SpendClothsGiftsExpenses,
    questions: 3,
  },
  {
    step: 13,
    Component: AdditionalExpensesCaregories,
    questions: 1,
    IS_SKIPPABLE: true
  },
  {
    step: 14,
    Component: AdditionalExpenses,
    IS_EDITABLE: true,
    questions: 1,
  },
  {
    step: 15,
    Component: Entertainment,
    questions: 1,
  },
  {
    step: 16,
    Component: SavingUp,
    questions: 1,
    IS_SKIPPABLE: true
  },
  {
    step: 17,
    Component: SavingsVacationsRetirementExpenses,
    IS_EDITABLE: true,
    questions: 1,
  },
  {
    step: 18,
    Component: LoansBackHomeExpenses,
    questions: 1,
  },
  {
    step: 19,
    Component: AboutDebt,
    IS_EDITABLE: true,
    questions: 1,
  },
  {
    step: 20,
    Component: FinalSteps,
    IS_FINAL: true,
    IS_REVIEW: true,
  },
];

const EditExpenses = () => {
  const [innerStepper, setInnerStepper] = useState(0);
  const dispatch = useDispatch();
  const foundItem = stepper.find((i) => i.step == innerStepper);
  const [editableStep, setEditableStep] = useState(null);
  const navigate = useNavigate();
  const containerRef = useRef();
  const { t } = useTranslation();
  const [debtObj, setDebtObj] = useState({
    title: "",
    totalDebt: "",
    minPay: "",
    interestRate: "",
  });

  const formik = useFormik({
    initialValues: {
      categories: [],
      mortgage: "",
      rentAmount: "",
      homeMaintenance: "",
      taxPay: "",
      condoFee: "",
      tenantInsurance: "",
      utilities: "",
      groceries: "",
      eatingOut: "",
      cellphoneBill: "",
      internetBill: "",
      subscriptions: "",
      supportFamily: "",
      incomeArr: [],
      savingsArr: [],
      expenseCategories: [],
      living: "",
      carOwn: "",
      carStatus: "",
      debtArr: [],
      currentStep: 0,
    },
    validateOnBlur: true,
    onSubmit: (values) => onFinish(values),
  });

  const getTotalAnswered = () => {
    let totalAnswered = 0;
    const {
      living,
      supportFamily,
      carOwn,
      carStatus,
      debtFlag,
      debtArr,
      mortgage,
      homeInsurance,
      homeMaintenance,
      condoFee,
      taxPay,
      rentAmount,
      tenantInsurance,
      utilities,
      groceries,
      eatingOut,
      subscriptions,
      cellphoneBill,
      internetBill,
      carInsurancePayment,
      carPayment,
      carMaintenance,
      carGasCost,
      transportationPay,
      clothingExpenses,
      giftingExpenses,
      footwearExpenses,
      entertainmentCost,
      expenseCategories,
      incomeArr,
      savingsArr,
      savingsAmtArr,
      supportAmount,
    } = formik.values;

    if (living) {
      totalAnswered += 1;
    }
    if (living === "own") {
      if (!isFalsyExceptZero(mortgage)) {
        totalAnswered += 1;
      }
      if (!isFalsyExceptZero(taxPay)) {
        totalAnswered += 1;
      }
      if (!isFalsyExceptZero(condoFee)) {
        totalAnswered += 1;
      }
      if (!isFalsyExceptZero(homeMaintenance)) {
        totalAnswered += 1;
      }
      if (!isFalsyExceptZero(homeInsurance)) {
        totalAnswered += 1;
      }
    }
    if (living === "rent" || living === "other") {
      if (!isFalsyExceptZero(rentAmount)) {
        totalAnswered += 1;
      }
      if (!isFalsyExceptZero(tenantInsurance)) {
        totalAnswered += 1;
      }
    }
    if (!isFalsyExceptZero(utilities)) {
      totalAnswered += 1;
    }
    if (!isFalsyExceptZero(groceries)) {
      totalAnswered += 1;
    }
    if (!isFalsyExceptZero(cellphoneBill)) {
      totalAnswered += 1;
    }
    if (!isFalsyExceptZero(internetBill)) {
      totalAnswered += 1;
    }
    if (!isFalsyExceptZero(eatingOut)) {
      totalAnswered += 1;
    }
    if (!isFalsyExceptZero(subscriptions)) {
      totalAnswered += 1;
    }
    if (supportFamily) {
      totalAnswered += 1;
      if (supportFamily === "yes" && !isFalsyExceptZero(supportAmount)) {
        totalAnswered += 1;
      }
    }
    if (carOwn) {
      totalAnswered += 1;
      if (carOwn === "yes") {
        if (carStatus === "finance" || carStatus === "lease") {
          if (!isFalsyExceptZero(carPayment)) {
            totalAnswered += 1;
          }
          if (!isFalsyExceptZero(carInsurancePayment)) {
            totalAnswered += 1;
          }
        } else if (carStatus === "nothing") {
          if (!isFalsyExceptZero(carInsurancePayment)) {
            totalAnswered += 1;
          }
        }
        if (!isFalsyExceptZero(carMaintenance)) {
          totalAnswered += 1;
        }
        if (!isFalsyExceptZero(carGasCost)) {
          totalAnswered += 1;
        }
      }
    }
    if (!isFalsyExceptZero(transportationPay)) {
      totalAnswered += 1;
    }

    if (!isFalsyExceptZero(clothingExpenses)) {
      totalAnswered += 1;
    }
    if (!isFalsyExceptZero(footwearExpenses)) {
      totalAnswered += 1;
    }
    if (!isFalsyExceptZero(giftingExpenses)) {
      totalAnswered += 1;
    }
    if (!isFalsyExceptZero(entertainmentCost)) {
      totalAnswered += 1;
    }
    if (expenseCategories?.length > 0) {
      totalAnswered += 1;
    }
    if (
      incomeArr?.length > 0 &&
      incomeArr.every((item) => (item.amount ? item.amount : false))
    ) {
      totalAnswered += 1;
    }
    if (savingsArr?.length > 0) {
      totalAnswered += 1;
    }
    if (savingsAmtArr?.length > 0) {
      const flag = savingsAmtArr.every((item) => {
        if (item?.isManuallyAdded) {
          return item.type && item.amount;
        } else {
          return item?.amount ? item?.amount : false;
        }
      });
      if (flag) {
        totalAnswered += 1;
      }
    }
    if (debtFlag) {
      totalAnswered += 1;
      if (debtFlag === "yes" && debtArr?.length > 0) {
        const flag = debtArr.every((item) => {
          return Object.values(item).every((value) => value !== "");
        });
        if (flag) {
          totalAnswered += 1;
        }
      }
    }
    dispatch(setAnsweredQuestions(totalAnswered));
  };

  const getTotalQuestions = () => {
    let totalQuestions = 0;
    const { living, supportFamily, carOwn, carStatus, debtFlag } =
      formik.values;
    if (living === "own") {
      totalQuestions += 5;
    }
    if (living === "rent" || living === "other") {
      totalQuestions += 2;
    }
    if (supportFamily === "yes") {
      totalQuestions += 1;
    }
    if (carOwn) {
      totalQuestions += 1;
    }
    if (carOwn === "yes" && carStatus === "nothing") {
      totalQuestions += 3;
    }
    if (
      carOwn === "yes" &&
      (carStatus === "finance" || carStatus === "lease")
    ) {
      totalQuestions += 4;
    }
    if (debtFlag === "yes") {
      totalQuestions += 1;
    }
    totalQuestions += 18;
    dispatch(setTotalQuestions(totalQuestions));
  };

  useEffect(() => {
    if (foundItem?.IS_REVIEW || !formik.values?.living) {
      fetchExpensesDetails();
    }
  }, [innerStepper]);
  useEffect(() => {
    getTotalQuestions();
    getTotalAnswered();
  }, [formik.values]);

  const fetchExpensesDetails = () => {
    dispatch(setLoading(true));
    const currentMonth = format(new Date(), "MM-yyyy");
    GetExpenseDetails({ currentMonth })
      .then((resp) => {
        const { data } = resp;
        if (Array?.isArray(data) && !data?.length) {
          formik.setFieldValue("currentStep", innerStepper);
        } else {
          formik.setValues({
            living: data?.isHomeRent,
            mortgage: String(
              isFalsyExceptZero(data?.mortgage) ? "" : data?.mortgage
            ),
            homeMaintenance: String(
              isFalsyExceptZero(data?.homeMaintenance)
                ? ""
                : data?.homeMaintenance
            ),
            taxPay: String(
              isFalsyExceptZero(data?.propertyTax) ? "" : data?.propertyTax
            ),
            condoFee: String(
              isFalsyExceptZero(data?.condoFee) ? "" : data?.condoFee
            ),
            rentAmount: String(
              isFalsyExceptZero(data?.RentAmount) ? "" : data?.RentAmount
            ),
            tenantInsurance: String(
              isFalsyExceptZero(data?.tenantInsurance)
                ? ""
                : data?.tenantInsurance
            ),
            homeInsurance: String(
              isFalsyExceptZero(data?.homeInsurance) ? "" : data?.homeInsurance
            ),
            entertainmentCost: String(
              isFalsyExceptZero(data?.entertainmentCost)
                ? ""
                : data?.entertainmentCost
            ),
            utilities: String(
              isFalsyExceptZero(data?.utilitiesAmount)
                ? ""
                : data?.utilitiesAmount
            ),
            groceries: String(
              isFalsyExceptZero(data?.groceriesCost) ? "" : data?.groceriesCost
            ),
            eatingOut: String(
              isFalsyExceptZero(data?.FoodCost) ? "" : data?.FoodCost
            ),
            cellphoneBill: String(
              isFalsyExceptZero(data?.mobileBill) ? "" : data?.mobileBill
            ),
            internetBill: String(
              isFalsyExceptZero(data?.internetCost) ? "" : data?.internetCost
            ),
            subscriptions: String(
              isFalsyExceptZero(data?.subscriptionBill)
                ? ""
                : data?.subscriptionBill
            ),
            supportFamily: data?.isSendbackHome,
            supportAmount: String(
              isFalsyExceptZero(data?.sendHomeAmount)
                ? ""
                : data?.sendHomeAmount
            ),
            carOwn: data?.ownCar,
            carStatus: data?.carStatus,
            carPayment: String(
              isFalsyExceptZero(data?.carPayment) ? "" : data?.carPayment
            ),
            carInsurancePayment: String(
              isFalsyExceptZero(data?.carInsurance) ? "" : data?.carInsurance
            ),
            carMaintenance: String(
              isFalsyExceptZero(data?.maintanenceCost)
                ? ""
                : data?.maintanenceCost
            ),
            carGasCost: String(
              isFalsyExceptZero(data?.gasCost) ? "" : data?.gasCost
            ),
            transportationPay: String(
              isFalsyExceptZero(data?.transportCost) ? "" : data?.transportCost
            ),
            clothingExpenses: String(
              isFalsyExceptZero(data?.clothesCost) ? "" : data?.clothesCost
            ),
            giftingExpenses: String(
              isFalsyExceptZero(data?.giftCost) ? "" : data?.giftCost
            ),
            footwearExpenses: String(
              isFalsyExceptZero(data?.footwearCost) ? "" : data?.footwearCost
            ),
            expenseCategories:
              data?.additionalExpense?.length > 0
                ? data?.additionalExpense?.map((i) => i.type)
                : [],
            incomeArr: data?.additionalExpense?.map((i) => ({
              ...i,
              amount: String(isFalsyExceptZero(i.amount) ? "" : i.amount),
            })),
            savingsArr:
              data?.currentSavingUp?.length > 0
                ? data?.currentSavingUp?.map((i) => i.type)
                : [],
            savingsAmtArr:
              data?.currentSavingUp?.length > 0
                ? data?.currentSavingUp?.map((i) => ({
                    ...i,
                    amount: String(isFalsyExceptZero(i.amount) ? "" : i.amount),
                  }))
                : [],
            debtFlag: data?.isDebt,
            debtArr:
              data?.debtDetails?.length > 0
                ? data?.debtDetails.map((i) => ({
                    ...i,
                    minPay: String(isFalsyExceptZero(i.minPay) ? "" : i.minPay),
                    totalDebt: String(
                      isFalsyExceptZero(i.totalDebt) ? "" : i.totalDebt
                    ),
                    interestRate: String(
                      isFalsyExceptZero(i.interestRate) ? "" : i.interestRate
                    ),
                  }))
                : [
                    {
                      title: "",
                      minPay: "",
                      interestRate: "",
                      totalDebt: "",
                      id: 1,
                    },
                  ],
            currentStep: 0,
          });
        }
      })
      .catch((Err) => {
        message.error(Err.message[i18n.language]);
      })
      .finally(() => {
        dispatch(setLoading(false));
      });
  };

  const handlePreviousStep = () => {
    const { living, carStatus, debtFlag, carOwn, savingsAmtArr, debtArr } =
      formik.values;
    if (innerStepper > 0) {
      if (living === "rent" && innerStepper === 3) {
        setInnerStepper(0);
        formik.setFieldValue("currentStep", 0);
      } else if (living === "own" && innerStepper == 4) {
        setInnerStepper(2);
        formik.setFieldValue("currentStep", 2);
      } else if (living === "other" && innerStepper == 3) {
        setInnerStepper(0);
        formik.setFieldValue("currentStep", 0);
      } else if (carOwn === "no" && innerStepper === 11) {
        setInnerStepper(7);
        formik.setFieldValue("currentStep", 7);
      } else if (carStatus === "nothing" && innerStepper === 9) {
        setInnerStepper(7);
        formik.setFieldValue("currentStep", 7);
      } else if (innerStepper === 20 && debtFlag === "no") {
        setInnerStepper(18);
        formik.setFieldValue("currentStep", 18);
        setEditableStep(null);
      } else if (
        innerStepper === 15 &&
        formik.values?.incomeArr?.length === 0
      ) {
        setInnerStepper(innerStepper - 2);
        formik.setFieldValue("currentStep", innerStepper - 2);
      } else if (innerStepper === 18) {
        if (savingsAmtArr?.length === 0) {
          setInnerStepper(innerStepper - 2);
          formik.setFieldValue("currentStep", innerStepper - 2);
        } else {
          setEditableStep(17);
          setInnerStepper(innerStepper - 1);
          formik.setFieldValue("currentStep", innerStepper - 1);
        }
      } else if (foundItem?.IS_REVIEW) {
        let flag = !debtArr.every((item) => {
          return Object.values(item).every((value) => value !== "");
        });
        if (flag) {
          setEditableStep(19);
        }
        setInnerStepper(innerStepper - 1);
        formik.setFieldValue("currentStep", innerStepper - 1);
      } else {
        setInnerStepper(innerStepper - 1);
        formik.setFieldValue("currentStep", innerStepper - 1);
        setEditableStep(null);
      }
    }
  };

  const checkSubmitDisabled = () => {
    const {
      living,
      mortgage,
      homeMaintenance,
      taxPay,
      condoFee,
      homeInsurance,
      rentAmount,
      utilities,
      groceries,
      eatingOut,
      cellphoneBill,
      internetBill,
      subscriptions,
      supportFamily,
      supportAmount,
      carOwn,
      carStatus,
      carPayment,
      carInsurancePayment,
      entertainmentCost,
      carMaintenance,
      carGasCost,
      transportationPay,
      clothingExpenses,
      giftingExpenses,
      footwearExpenses,
      debtFlag,
      debtArr,
      expenseCategories,
      savingsArr,
      currentStep,
    } = formik.values;
    if ((currentStep === 0 || foundItem?.IS_REVIEW) && !living) {
      return true;
    } else if (
      (currentStep === 1 || foundItem?.IS_REVIEW) &&
      living === "own" &&
      !mortgage
    ) {
      return true;
    } else if (
      (currentStep === 2 || foundItem?.IS_REVIEW) &&
      living === "own" &&
      (isFalsyExceptZero(homeMaintenance) ||
        isFalsyExceptZero(taxPay) ||
        isFalsyExceptZero(condoFee) ||
        isFalsyExceptZero(homeInsurance))
    ) {
      return true;
    } else if (
      currentStep === 3 &&
      living === "rent" &&
      isFalsyExceptZero(rentAmount)
    ) {
      return true;
    } else if (
      (currentStep === 4 || foundItem?.IS_REVIEW) &&
      isFalsyExceptZero(utilities)
    ) {
      return true;
    } else if (
      (currentStep === 5 || foundItem?.IS_REVIEW) &&
      (isFalsyExceptZero(groceries) ||
        isFalsyExceptZero(eatingOut) ||
        isFalsyExceptZero(internetBill) ||
        isFalsyExceptZero(cellphoneBill) ||
        isFalsyExceptZero(subscriptions))
    ) {
      return true;
    } else if (
      (currentStep === 6 || foundItem?.IS_REVIEW) &&
      (!supportFamily ||
        (supportFamily === "yes" && isFalsyExceptZero(supportAmount)))
    ) {
      return true;
    } else if ((currentStep === 7 || foundItem?.IS_REVIEW) && !carOwn) {
      return true;
    } else if (
      (currentStep === 7 || foundItem?.IS_REVIEW) &&
      carOwn === "yes" &&
      !carStatus
    ) {
      return true;
    } else if (
      (currentStep === 8 || foundItem?.IS_REVIEW) &&
      carStatus &&
      carStatus != "nothing" &&
      isFalsyExceptZero(carPayment)
    ) {
      return true;
    } else if (
      (currentStep === 9 || foundItem?.IS_REVIEW) &&
      carOwn === "yes" &&
      carStatus &&
      carStatus != "nothing" &&
      isFalsyExceptZero(carInsurancePayment)
    ) {
      return true;
    } else if (
      (currentStep === 10 || foundItem?.IS_REVIEW) &&
      carOwn === "yes" &&
      carStatus &&
      carStatus != "nothing" &&
      (isFalsyExceptZero(carMaintenance) || isFalsyExceptZero(carGasCost))
    ) {
      return true;
    } else if (currentStep === 11 && isFalsyExceptZero(transportationPay)) {
      return true;
    } else if (
      (currentStep === 12 || foundItem?.IS_REVIEW) &&
      (isFalsyExceptZero(clothingExpenses) ||
        isFalsyExceptZero(footwearExpenses) ||
        isFalsyExceptZero(giftingExpenses))
    ) {
      return true;
    } else if (currentStep === 15 && isFalsyExceptZero(entertainmentCost)) {
      return true;
    } else if (
      currentStep === 13 &&
      typeof expenseCategories != "undefined" &&
      expenseCategories?.length === 0
    ) {
      return true;
    } else if (
      currentStep === 16 &&
      typeof savingsArr != "undefined" &&
      savingsArr?.length === 0
    ) {
      return true;
    } else if ((currentStep === 18 || foundItem?.IS_REVIEW) && !debtFlag) {
      return true;
    } else if (
      (currentStep === 19 || foundItem?.IS_REVIEW) &&
      debtFlag === "yes" &&
      debtArr?.length === 0
    ) {
      return true;
    }
    return false;
  };

  const checkConfirmDisabled = () => {
    const { currentStep, incomeArr, savingsAmtArr, debtArr, debtFlag } =
      formik.values;
    let flag = false;
    if ((currentStep === 14 || foundItem?.IS_REVIEW) && incomeArr?.length > 0) {
      flag = !incomeArr.every((item) => (item.amount ? item.amount : false));
      if (flag) {
        return flag;
      }
    }
    if (
      (currentStep === 17 || foundItem?.IS_REVIEW) &&
      savingsAmtArr?.length > 0
    ) {
      flag = !savingsAmtArr.every((item) => {
        if (item?.isManuallyAdded) {
          return item.type && item.amount;
        } else {
          return item?.amount ? item?.amount : false;
        }
      });
      if (flag) {
        return flag;
      }
    }
    if (
      (currentStep === 19 || foundItem?.IS_REVIEW) &&
      debtFlag === "yes" &&
      debtArr?.length > 0
    ) {
      flag = !debtArr.every((item) => {
        return Object.values(item).every((value) => value !== "");
      });
      if (flag) {
        return flag;
      }
    }
    return flag;
  };
    const getStep = () => {
    const {
      living,
      carStatus,
      debtFlag,
      carOwn,
      savingsArr,
    } = formik.values;
    if (!foundItem?.IS_FINAL) {
      
      if ((living === "rent" || living === "other") && innerStepper === 0) {
        return 3;
      } else if (living === "own" && innerStepper === 1) {
        return 2;
      } else if (living === "own" && innerStepper == 2) {
        return 4;
      } else if (carOwn === "no" && innerStepper === 7) {
        return 11;
      } else if (carStatus === "nothing" && innerStepper === 7) {
        return 9;
      } else if (innerStepper === 13) {
        return innerStepper + 1;
      } else if (innerStepper === 16) {
        if (savingsArr?.length === 0) {
          return innerStepper + 2;
        } else {
          return innerStepper + 1;
        }
      } else if (debtFlag === "no" && innerStepper === 18) {
        return 20;
      } else if (debtFlag === "yes" && innerStepper === 18) {
        return innerStepper + 1;
      } else {
        return innerStepper + 1;
      }
    }
  };

  const getPayload = (IS_SKIP) => {
    const {
      living,
      mortgage,
      homeMaintenance,
      taxPay,
      condoFee,
      rentAmount,
      tenantInsurance,
      utilities,
      groceries,
      eatingOut,
      cellphoneBill,
      internetBill,
      entertainmentCost,
      homeInsurance,
      subscriptions,
      supportFamily,
      supportAmount,
      carOwn,
      carStatus,
      carPayment,
      carInsurancePayment,
      carMaintenance,
      carGasCost,
      transportationPay,
      clothingExpenses,
      giftingExpenses,
      footwearExpenses,
      incomeArr,
      savingsAmtArr,
      debtFlag,
      debtArr,
    } = formik.values;
    const currentMonth = format(new Date(), "MM-yyyy");
    const obj = {};
    obj.step =
      editableStep === 14 || editableStep === 17 ? innerStepper : getStep();
    obj.currentMonth = currentMonth;
    obj.isEdit = true;
    if (innerStepper === 0) {
      obj.isHomeRent = living;
      obj.mortgage = Number(String(mortgage)?.replace(/,/g, ""));
      obj.homeInsurance = Number(String(homeInsurance)?.replace(/,/g, ""));
      obj.homeMaintenance = Number(String(homeMaintenance)?.replace(/,/g, ""));
      obj.propertyTax = Number(String(taxPay)?.replace(/,/g, ""));
      obj.condoFee = Number(String(condoFee)?.replace(/,/g, ""));
      obj.RentAmount = Number(String(rentAmount)?.replace(/,/g, ""));
      obj.tenantInsurance = Number(String(tenantInsurance)?.replace(/,/g, ""));
    } else if (innerStepper === 1) {
      obj.mortgage = Number(String(mortgage)?.replace(/,/g, ""));
    } else if (innerStepper === 2) {
      obj.homeInsurance = Number(String(homeInsurance)?.replace(/,/g, ""));
      obj.homeMaintenance = Number(String(homeMaintenance)?.replace(/,/g, ""));
      obj.propertyTax = Number(String(taxPay)?.replace(/,/g, ""));
      obj.condoFee = Number(String(condoFee)?.replace(/,/g, ""));
    } else if (innerStepper === 3) {
      obj.RentAmount = Number(String(rentAmount)?.replace(/,/g, ""));
      obj.tenantInsurance = Number(String(tenantInsurance)?.replace(/,/g, ""));
    } else if (innerStepper === 4) {
      obj.utilitiesAmount = Number(String(utilities)?.replace(/,/g, ""));
    } else if (innerStepper === 5) {
      obj.groceriesCost = Number(String(groceries)?.replace(/,/g, ""));
      obj.FoodCost = Number(String(eatingOut)?.replace(/,/g, ""));
      obj.internetCost = Number(String(internetBill)?.replace(/,/g, ""));
      obj.mobileBill = Number(String(cellphoneBill)?.replace(/,/g, ""));
      obj.subscriptionBill = Number(String(subscriptions)?.replace(/,/g, ""));
    } else if (innerStepper === 6) {
      obj.isSendbackHome = supportFamily;
      obj.sendHomeAmount = Number(String(supportAmount)?.replace(/,/g, ""));
    } else if (innerStepper === 7) {
      obj.ownCar = carOwn;
      obj.carStatus = carStatus;
      obj.carPayment = Number(String(carPayment)?.replace(/,/g, ""));
      obj.carInsurance = Number(String(carInsurancePayment)?.replace(/,/g, ""));
      obj.maintanenceCost = Number(String(carMaintenance)?.replace(/,/g, ""));
      obj.gasCost = Number(String(carGasCost)?.replace(/,/g, ""));
    } else if (innerStepper === 8) {
      obj.carPayment = Number(String(carPayment)?.replace(/,/g, ""));
    } else if (innerStepper === 9) {
      obj.carInsurance = Number(String(carInsurancePayment)?.replace(/,/g, ""));
    } else if (innerStepper === 10) {
      obj.maintanenceCost = Number(String(carMaintenance)?.replace(/,/g, ""));
      obj.gasCost = Number(String(carGasCost)?.replace(/,/g, ""));
    } else if (innerStepper === 11) {
      obj.transportCost = IS_SKIP
        ? ""
        : Number(String(transportationPay)?.replace(/,/g, ""));
    } else if (innerStepper === 12) {
      obj.clothesCost = Number(String(clothingExpenses)?.replace(/,/g, ""));
      obj.footwearCost = Number(String(footwearExpenses)?.replace(/,/g, ""));
      obj.giftCost = Number(String(giftingExpenses)?.replace(/,/g, ""));
    } else if (innerStepper === 13 && IS_SKIP) {
      obj.additionalExpense = [];
    } else if (innerStepper === 14) {
      obj.additionalExpense = incomeArr?.map((i) => ({
        ...i,
        type: i?.type?.en || i?.type,
        amount: Number(String(i?.amount)?.replace(/,/g, "")),
      }));
    } else if (innerStepper === 15) {
      obj.entertainmentCost = IS_SKIP
        ? ""
        : Number(String(entertainmentCost)?.replace(/,/g, ""));
    } else if (innerStepper === 16 && IS_SKIP) {
      obj.currentSavingUp = [];
    } else if (innerStepper === 17) {
      obj.currentSavingUp = savingsAmtArr?.map((i) => {
        const otherOpt = i.type?.en || i.type;
        return {
          ...i,
          title:
            i?.isManuallyAdded || otherOpt?.includes("Other") ? i.title : "",
          type: i.isManuallyAdded ? i.type : otherOpt,
          amount: Number(String(i.amount)?.replace(/,/g, "")),
        };
      });
    } else if (innerStepper === 18) {
      obj.isDebt = debtFlag;
    } else if (innerStepper === 19) {
      obj.debtDetails =
        debtFlag === "no"
          ? []
          : debtArr?.map((i) => ({
              ...i,
              totalDebt: Number(String(i?.totalDebt)?.replace(/,/g, "")),
              minPay: Number(String(i.minPay)?.replace(/,/g, "")),
              interestRate: Number(String(i.interestRate)?.replace(/,/g, "")),
            }));
    } else if (innerStepper === 20) {
      obj.isHomeRent = living;
      obj.mortgage = Number(String(mortgage)?.replace(/,/g, ""));
      obj.homeMaintenance = Number(String(homeMaintenance)?.replace(/,/g, ""));
      obj.propertyTax = Number(String(taxPay)?.replace(/,/g, ""));
      obj.condoFee = Number(String(condoFee)?.replace(/,/g, ""));
      obj.homeInsurance = Number(String(homeInsurance)?.replace(/,/g, ""));
      obj.RentAmount = Number(String(rentAmount)?.replace(/,/g, ""));
      obj.tenantInsurance = Number(String(tenantInsurance)?.replace(/,/g, ""));
      obj.utilitiesAmount = Number(String(utilities)?.replace(/,/g, ""));
      obj.groceriesCost = Number(String(groceries)?.replace(/,/g, ""));
      obj.FoodCost = Number(String(eatingOut)?.replace(/,/g, ""));
      obj.internetCost = Number(String(internetBill)?.replace(/,/g, ""));
      obj.mobileBill = Number(String(cellphoneBill)?.replace(/,/g, ""));
      obj.subscriptionBill = Number(String(subscriptions)?.replace(/,/g, ""));
      obj.isSendbackHome = supportFamily;
      obj.sendHomeAmount = Number(String(supportAmount)?.replace(/,/g, ""));
      obj.ownCar = carOwn;
      obj.carStatus = carStatus;
      obj.carPayment = Number(String(carPayment)?.replace(/,/g, ""));
      obj.carInsurance = Number(String(carInsurancePayment)?.replace(/,/g, ""));
      obj.maintanenceCost = Number(String(carMaintenance)?.replace(/,/g, ""));
      obj.gasCost = Number(String(carGasCost)?.replace(/,/g, ""));
      obj.transportCost = Number(String(transportationPay)?.replace(/,/g, ""));
      obj.clothesCost = Number(String(clothingExpenses)?.replace(/,/g, ""));
      obj.footwearCost = Number(String(footwearExpenses)?.replace(/,/g, ""));
      obj.giftCost = Number(String(giftingExpenses)?.replace(/,/g, ""));
      obj.additionalExpense = incomeArr?.map((i) => ({
        ...i,
        type: i?.type?.en || i?.type,
        amount: Number(String(i?.amount)?.replace(/,/g, "")),
      }));
      obj.entertainmentCost = Number(
        String(entertainmentCost)?.replace(/,/g, "")
      );
      obj.currentSavingUp = savingsAmtArr?.map((i) => {
        const otherOpt = i.type?.en || i.type;
        return {
          ...i,
          title:
            i?.isManuallyAdded || otherOpt?.includes("Other") ? i.title : "",
          type: i.isManuallyAdded ? i.type : otherOpt,
          amount: Number(String(i.amount)?.replace(/,/g, "")),
        };
      });
      obj.isDebt = debtFlag;
      obj.debtDetails =
        debtFlag === "no"
          ? []
          : debtArr?.map((i) => {
              return {
                ...i,
                totalDebt: Number(String(i?.totalDebt)?.replace(/,/g, "")),
                minPay: Number(String(i.minPay)?.replace(/,/g, "")),
                interestRate: Number(String(i.interestRate)?.replace(/,/g, "")),
              };
            });
      obj.isExpenseCompleted = true;
    }
    return obj;
  };

  const getFilteredArr = () => {
    let filteredArr = [...stepper].filter((i) => !i.IS_FINAL);
    const { living, carStatus, debtFlag, expenseCategories, carOwn } =
      formik.values;

    if (living === "rent") {
      filteredArr = filteredArr.filter((i) => i.step != 1 && i.step != 2);
    } else if (living === "own") {
      filteredArr = filteredArr.filter((i) => i.step != 3);
    } else if (living === "other") {
      filteredArr = filteredArr.filter((i) => i.step != 1 && i.step != 2);
    }
    if (expenseCategories?.length === 0) {
      filteredArr = filteredArr.filter((i) => i.step != 14);
    }
    if (carOwn === "no") {
      filteredArr = filteredArr.filter(
        (i) => i.step != 8 && i.step != 9 && i.step != 10
      );
    }
    if (carStatus === "nothing") {
      filteredArr = filteredArr.filter((i) => i.step != 8);
    }
    if (debtFlag === "no") {
      filteredArr = filteredArr.filter((i) => i.step != 19);
    }
    return filteredArr;
  };
  const handleNextStep = (IS_SKIP) => {
    
    const { living, carStatus, debtFlag, carOwn } =
      formik.values;
    if (!foundItem?.IS_REVIEW) {
      if ((living === "rent" || living === "other") && innerStepper === 0) {
        setInnerStepper(3);
        formik.setFieldValue("currentStep", 3);
      } else if (living === "own" && innerStepper === 1) {
        setInnerStepper(2);
        formik.setFieldValue("currentStep", 2);
      } else if (living === "own" && innerStepper == 2) {
        setInnerStepper(4);
        formik.setFieldValue("currentStep", 4);
      } else if (carOwn === "no" && innerStepper === 7) {
        setInnerStepper(11);
        formik.setFieldValue("currentStep", 11);
      } else if (carStatus === "nothing" && innerStepper === 7) {
        setInnerStepper(9);
        formik.setFieldValue("currentStep", 9);
      } else if (innerStepper === 11 && IS_SKIP) {
        formik.setFieldValue("transportationPay", "");
        setInnerStepper(innerStepper + 1);
        formik.setFieldValue("currentStep", innerStepper + 1);
      } else if (innerStepper === 13) {
        if (IS_SKIP) {
          setInnerStepper(innerStepper + 2);
          formik.setFieldValue("currentStep", innerStepper + 2);
          formik.setFieldValue("incomeArr", []);
          formik.setFieldValue("expenseCategories", []);
        } else {
          setEditableStep(14);
          setInnerStepper(innerStepper + 1);
          formik.setFieldValue("currentStep", innerStepper + 1);
        }
      } else if (innerStepper === 15 && IS_SKIP) {
        formik.setFieldValue("entertainmentCost", "");
        setInnerStepper(innerStepper + 1);
        formik.setFieldValue("currentStep", innerStepper + 1);
      } else if (innerStepper === 16) {
        if (IS_SKIP) {
          setInnerStepper(innerStepper + 2);
          formik.setFieldValue("currentStep", innerStepper + 2);
          formik.setFieldValue("savingsArr", []);
          formik.setFieldValue("savingsAmtArr", []);
        } else {
          setEditableStep(17);
          setInnerStepper(innerStepper + 1);
          formik.setFieldValue("currentStep", innerStepper + 1);
        }
      } else if (debtFlag === "no" && innerStepper === 18) {
        setInnerStepper(20);
        formik.setFieldValue("currentStep", 20);
      } else if (debtFlag === "yes" && innerStepper === 18) {
        setEditableStep(19);
        setInnerStepper(innerStepper + 1);
        formik.setFieldValue("currentStep", innerStepper + 1);
      } else {
        setInnerStepper(innerStepper + 1);
        formik.setFieldValue("currentStep", innerStepper + 1);
      }
    } else {
      navigate(routePaths?.dashboard);
    }
  };

  const onFinish = (values, IS_SKIP) => {
    dispatch(setLoading(true));
    const payload = getPayload(IS_SKIP);
    UpdateExpenses(payload)
      .then((resp) => {
        if (editableStep != 14 && editableStep != 17 && editableStep != 19) {
          handleNextStep(IS_SKIP);
        }
      })
      .catch((Err) => {
        message.error(Err.message[i18n.language]);
      })
      .finally(() => {
        dispatch(setLoading(false));
      });
  };
  const handleKeyPress = (event) => {
    // Prevent 'e', 'E', '+', and '-' characters
    if (["e", "E", "+", "-"].includes(event.key)) {
      event.preventDefault();
    }
  };
  useEffect(() => {
    if (containerRef) {
      containerRef.current.scrollTo({
        behavior: "smooth",
        block: "start",
        inline: "nearest",
      });
    }
  }, [containerRef, innerStepper]);
  return (
    <div
      className={`income-details-right ${
        foundItem?.step === 0 || foundItem?.IS_FINAL ? "previous-hidden" : ""
      }`}
      ref={containerRef}
    >
      {/*header*/}
      <form onSubmit={formik.handleSubmit}>
        <div className="only-mobile-show">
          <Button
            className="previous-btn w-auto d-flex align-items-center border-0 shadow-none rounded-0 bg-transparent"
            onClick={handlePreviousStep}
          >
            <img
              src="/assets/images/icons/back-arrow-gray-icon.svg"
              className="img-fluid"
              alt=""
            />
            {t("prevQues")}
          </Button>
        </div>
        {innerStepper != 0 && (
          <CreateProfileHeader
            handlePreviousStep={handlePreviousStep}
            step={foundItem?.step}
            isEdit={true}
          />
        )}
        <CreateProfileCenterWrapper>
          {innerStepper === 20 ? (
            <div className="row">
              <div className="col-12 review-inform-div">
                <h6 className="review-tag">{t("reviewInfo")}</h6>
                {getFilteredArr()?.map((Item) => {
                  return (
                    <Item.Component
                      onFinish={onFinish}
                      formik={formik}
                      handleEditableStep={(num) => setEditableStep(num)}
                      editableStep={editableStep}
                      IS_EDITABLE={foundItem?.IS_EDITABLE}
                      debtObj={debtObj}
                      handleDebtObj={(data) => setDebtObj(data)}
                      IS_REVIEW={true}
                      handleKeyPress={handleKeyPress}
                      setInnerStepper={setInnerStepper}
                      key={Item.step}
                    />
                  );
                })}
              </div>
            </div>
          ) : foundItem ? (
            <foundItem.Component
              onFinish={onFinish}
              formik={formik}
              handleEditableStep={(num) => setEditableStep(num)}
              editableStep={editableStep}
              IS_EDITABLE={foundItem?.IS_EDITABLE}
              debtObj={debtObj}
              handleDebtObj={(data) => setDebtObj(data)}
              innerStepper={innerStepper}
              handleKeyPress={handleKeyPress}
              setInnerStepper={setInnerStepper}
            />
          ) : (
            <></>
          )}
        </CreateProfileCenterWrapper>
        <CreateProfileFooter
          checkSubmitDisabled={checkSubmitDisabled}
          IS_EDITABLE={foundItem?.IS_EDITABLE}
          handleEditableStep={setEditableStep}
          editableStep={editableStep}
          debtObj={debtObj}
          IS_SKIPPABLE={foundItem?.IS_SKIPPABLE}
          emptyDebtObj={() => setDebtObj(null)}
          formik={formik}
          handleNextStep={handleNextStep}
          innerStepper={innerStepper}
          checkConfirmDisabled={checkConfirmDisabled}
          IS_REVIEW={foundItem?.IS_REVIEW}
          onFinish={onFinish}
        />
      </form>
    </div>
  );
};

export default EditExpenses;
